// =============================================================================
// NewBookingPage.jsx
// =============================================================================
//
// Manual booking form. Single-page, scroll-down. Sections in order:
//
//   1. Customer       — search existing OR create new (smart email-blur detect)
//   2. Booker         — within selected customer, or create new
//   3. Passenger      — within selected customer, or "I'm the passenger"
//   4. Ride details   — pickup/dropoff/time/class/mode + flight info  [Round 2]
//   5. Pricing        — calculate button + editable total + breakdown  [Round 2]
//   6. Payment        — radio per method + conditional details         [Round 3]
//   7. Internal notes — free text                                      [Round 3]
//   8. Submit         — POST /bookings/manual → redirect to detail     [Round 3]
//
// State design: this page owns the entire form state in a single object.
// Sections receive the slice they care about + an updater callback. Keeps
// the validation + submit logic centralised in one place rather than
// scattered across components.
// =============================================================================
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Search,
    UserCircle,
    Building2,
    Plus,
    X,
    AlertCircle,
    CheckCircle2,
    User as UserIcon,
    Edit2,
    MapPin,
    Plane,
    Calendar,
    Clock,
    Car,
    Calculator,
    Copy,
    Loader2,
    ArrowRight,
    Mail,
    FileText,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input, Select } from '../../components/ui/Form.jsx';
import AddressInput from '../../components/AddressInput.jsx';
import { customerDisplayName, customerContactPerson } from '../../lib/customer.js';
import EmailPreviewModal from '../../components/email/EmailPreviewModal.jsx';

// Pull postcode from address meta (preferred) or regex out of the address string.
function extractPostcode(meta, addressStr) {
    if (meta?.postcode) return meta.postcode;
    if (!addressStr) return null;
    const m = String(addressStr).toUpperCase().match(/[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/);
    return m ? m[0].replace(/\s+/g, ' ') : null;
}

// =============================================================================
// Top-level state shape
// =============================================================================
//
// `customer`:
//   { mode: 'pick' | 'create', selected: {...} | null, draft: {...} }
//   - 'pick' = dispatcher is searching/picking from existing customers
//   - 'create' = dispatcher is filling in a new customer's details
//   - selected = the resolved customer object (if mode=pick + a row was chosen)
//   - draft = field values for the create form (if mode=create)
//
// `booker`:
//   { mode: 'pick' | 'create', selected: {...} | null, draft: {...} }
//   - Only available once a customer is resolved.
//
// `passenger`:
//   { mode: 'pick' | 'create' | 'same_as_booker', selected: {...} | null, draft: {...} }
//
// `ride`, `pricing`, `payment`, `internalNotes` arrive in Rounds 2 and 3.
// =============================================================================

const EMPTY_CUSTOMER_DRAFT = {
    customer_type: '',
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
};

const EMPTY_BOOKER_DRAFT = {
    name: '',
    email: '',
    phone: '',
};

const EMPTY_PASSENGER_DRAFT = {
    name: '',
    phone: '',
    email: '',
};

// Sensible defaults for the ride section. Pickup date defaults to tomorrow
// (most common phone-in case is for a future booking, not "right now"), time
// to 09:00 (the dispatcher will overwrite anyway).
//
// Mode rules — matching booking engine behaviour:
//   - oneway: NO stops allowed. Pickup → drop-off, single leg.
//   - hourly: stops allowed, each with optional time. Hours derived from
//     pickup_time + final_time + driving distances (if any stop has a time
//     set, the engine recalculates expected final time from that stop +
//     remaining driving). See deriveHourlyBilling() for the full logic.
//
// Stop shape: { address, time, meta }. `time` is 'HH:MM' or empty string.
// `meta` is the full geocoded place object (from AddressInput) — used for
// CC zone determination so any stop within the zone triggers the charge.
const EMPTY_RIDE = {
    mode: 'oneway',                   // 'oneway' | 'hourly'
    pickup_address: '',               // canonical display string
    pickup_address_meta: null,        // { latitude, longitude, postcode, ... }
    dropoff_address: '',
    dropoff_address_meta: null,
    stops: [],                        // hourly-only: [{ address, time }]
    pickup_date: tomorrow(),          // YYYY-MM-DD
    pickup_time: '10:00',             // HH:MM — engine default (line 1281-1282)
    final_time: '10:00',              // HH:MM — engine default (line 1283-1284). Same as pickup → 2hr minimum applies until user changes.
    vehicle_class: 'business',        // 'business' | 'first' | 'suv' | 'xl'
    pax_count: 1,
        pax_comments: '',
    chauffeur_notes: '',
    flight_number: '',
    tail_number: '',
    fbo_code: '',
    customer_reference: '',           // optional PO / cost-centre / internal ref. Per-ride.
    show_flight_fields: false,        // toggle to reveal the airport/jet block
};

// Pricing state. `quote` holds whatever /pricing/calculate returned last
// (so we can show the breakdown), `manual_price` is the value in the
// editable price input. They diverge if the dispatcher overrides.
//
// `journey` holds Google's human-readable text strings ("45 mins", "30.2 mi")
// captured from Distance Matrix. Used purely for display in the breakdown —
// not tied to the billing values, which may be floored at 120 min minimum.
// Pricing state. `quote` holds whatever /pricing/calculate returned last
// (so we can show the breakdown), `manual_price` is the value in the
// editable price input. They diverge if the dispatcher overrides.
//
// `journey` holds Google's human-readable text strings ("45 mins", "30.2 mi")
// captured from Distance Matrix. Used purely for display in the breakdown —
// not tied to the billing values, which may be floored at 120 min minimum.
const EMPTY_PRICING = {
    quote: null,                      // full PricingService response or null
    manual_price: '',                 // string in the input (£ amount)
    is_calculating: false,
    calc_error: null,
    journey: null,                    // { duration_text, distance_text, raw_minutes } or null
    manually_overridden: false,       // dispatcher chose "Set price manually" — skip Calculate gate
};

// Payment state — chosen method + method-specific details.
//
//   external: requires external_note (e.g. "Cash on collection")
//   account:  customer-flagged only, no extra fields
//   saved_card: requires charge_timing (immediate / on_completion)
//   payment_link: requires payment_link_expires_at (default now+24h)
const EMPTY_PAYMENT = {
    method: '',                    // '' | 'saved_card' | 'payment_link' | 'account' | 'external'
    external_note: '',
    charge_timing: '',             // populated when method = saved_card
    payment_link_expires_at: '',   // ISO string, populated when method = payment_link
};


export function NewBookingPage() {
    const navigate = useNavigate();
    const api = useApi();
    const queryClient = useQueryClient();
    const { isAdmin } = useCurrentUser();

    // Mode: 'booking' (default) | 'quote'.
    // Quote mode hides Customer / Booker / Passenger / Payment sections and
    // shows an "Email quote to" section after ride+pricing instead. URL
    // param ?mode=quote bootstraps from a fresh "Quote" link in the sidebar
    // or from external integrations later. Switching mode preserves ride
    // and pricing state but clears customer-side state when going to quote
    // (and vice versa is fine — they coexist in state, just hidden).
    const [searchParams, setSearchParams] = useSearchParams();
    const initialMode = searchParams.get('mode') === 'quote' ? 'quote' : 'booking';
    const [mode, setMode] = useState(initialMode);

    function switchMode(newMode) {
        if (newMode === mode) return;
        setMode(newMode);
        // Reflect in URL so back-button + share-link work.
        const next = new URLSearchParams(searchParams);
        if (newMode === 'quote') next.set('mode', 'quote');
        else next.delete('mode');
        setSearchParams(next, { replace: true });
        // Reset step-by-step reveal — sections are different per mode, so
        // pretending the booker section is "revealed" makes no sense in
        // quote mode and vice versa.
        setRevealed(0);
    }

// Quote-side state. Used only in quote mode (or when "Email as quote"
    // is clicked from booking mode — see EmailAsQuoteModal).
    const [quoteRecipient, setQuoteRecipient] = useState({
        email: '',
                name: '',
        validity_days: 7,
        notes: '',
    });

    // "Email as quote" modal (Pattern 2 — fired from booking mode mid-form).
    const [emailQuoteModalOpen, setEmailQuoteModalOpen] = useState(false);

    // After "Email quote" on create flow: hold the just-created draft so the
    // preview modal can render + fire /quotes/:id/send. Cleared on close.
    const [previewQuote, setPreviewQuote] = useState(null);

    // Slice 7d.4 — convert-from-quote linkage. When set, the form prefills
    // from a quote and submitting includes from_quote_id so the backend
    // atomically converts the quote as part of POST /bookings/manual.
    //
    // convertingFromQuote: { quote_id, quote_ref } once successfully loaded.
    // quoteLoadError: { code, quote_ref?, message } if load failed (expired,
    //   cancelled, network, etc.). Banner shows but form remains usable —
    //   dispatcher can carry on with a fresh booking.
    const [convertingFromQuote, setConvertingFromQuote] = useState(null);
    const [quoteLoadError, setQuoteLoadError] = useState(null);

    const [customer, setCustomer] = useState({
        mode: 'pick',
        selected: null,
        draft: EMPTY_CUSTOMER_DRAFT,
    });

    const [booker, setBooker] = useState({
        mode: 'pick',
        selected: null,
        draft: EMPTY_BOOKER_DRAFT,
    });

    const [passenger, setPassenger] = useState({
        mode: 'same_as_booker',  // most common case → default
        selected: null,
        draft: EMPTY_PASSENGER_DRAFT,
    });

// Multi-ride basket. rides[0] is the first ride (always exists);
    // rides[1..N] are added via "Add another ride" in the basket UI.
    // Each entry has the same shape as the old singular `ride` object.
    const [rides, setRides] = useState([EMPTY_RIDE]);

    // Per-ride pricing state. Indexed 1:1 with rides[]. New rides push
    // a fresh EMPTY_PRICING; removing a ride removes its slot.
    const [pricings, setPricings] = useState([EMPTY_PRICING]);

// Master charged total — what the customer actually pays. Defaults
    // to sum of per-ride manual_prices; dispatcher can override (e.g.
    // goodwill discount on a 3-ride itinerary). Stored as a string for
    // the editable input. Only relevant when rides.length > 1.
    const [masterTotal, setMasterTotal] = useState('');

    // Whether the dispatcher has typed in the master total field. Until
    // they do, we keep masterTotal in sync with the sum of per-ride
    // prices as rides are added/removed/repriced. Once they take ownership
    // (typed a value or hit "Use sum"), we stop auto-syncing — that value
    // is what gets charged.
    const [masterTotalUserEdited, setMasterTotalUserEdited] = useState(false);

    // Per-ride live billing (hourly mode only). Indexed 1:1 with rides[].
    // Each ride's hourly billing computes independently — different routes
    // mean different Distance Matrix calls.
    const [liveBillings, setLiveBillings] = useState([{ billedHours: 2, miles: null }]);

    // Index of the ride currently being edited in the basket (-1 = none,
    // i.e. all rides collapsed and "+ Add another ride" / "Continue to
    // payment" affordances are showing).
 const [activeRideIdx, setActiveRideIdx] = useState(0);

    const [payment, setPayment] = useState(EMPTY_PAYMENT);

    // Saved addresses for the picker — pulled from the resolved customer.
    // Empty array until a customer is picked. Sorted newest-first; cap at
    // 5 in the picker UI (AddressInput renders all passed in, so we cap
    // here). Only `customer.selected` has addresses (existing customers);
    // create-mode customers don't yet exist in the DB so have none.
    const savedAddresses = useMemo(() => {
        const list = customer.selected?.addresses || [];
        // Sort by last_used_at DESC NULLS LAST, then created_at DESC.
        // Both fields are optional in payload — tolerate missing.
        const sorted = [...list].sort((a, b) => {
            const al = a.last_used_at || a.created_at || '';
            const bl = b.last_used_at || b.created_at || '';
            return bl.localeCompare(al);
        });
 return sorted.slice(0, 5);
    }, [customer.selected]);

    // Saved chauffeur notes for the picker — same source as addresses
    // (customer.selected). Empty until a customer is picked. No cap; a
    // customer rarely has more than a handful.
    const savedNotes = useMemo(() => {
        return customer.selected?.saved_notes || [];
    }, [customer.selected]);

    // Submit state — track in-flight submission and any error from the API.
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Per-section expand/collapse state. All six sections tracked here.
    // On initial render, only customer is expanded; others are revealed
    // and expanded one at a time as dispatcher clicks Continue.
    //
    // Any section can be re-opened individually by clicking its collapsed
    // line (or Edit affordance). Re-opening doesn't affect siblings.
const [expanded, setExpanded] = useState({
        customer: true,
        booker: false,
        passenger: false,
        ride_pricing: false,
        payment: false,
        email_quote: false,
    });

function expandOnly(section) {
        // Open one section, leave others as they are. Used for "click to edit"
        // on a collapsed summary — we don't want re-opening one to also collapse
        // the section currently being worked on.
        setExpanded((prev) => ({ ...prev, [section]: true }));
    }

    function collapse(section) {
        setExpanded((prev) => ({ ...prev, [section]: false }));
    }

    // -----------------------------------------------------------------------
    // Basket helpers — add/remove/update rides and pricings together
    // -----------------------------------------------------------------------
    //
    // rides[] and pricings[] are kept index-aligned. Any operation that
    // changes the rides array length must do the same to pricings (and
    // liveBillings). Helpers below enforce that.
    //
    // Passenger default for new rides: copy pax_name / pax_phone / pax_count
    // from ride[0] (the booking-level passenger resolution lands there at
    // submit time). Dispatcher can override per-ride. Mirrors the engine's
    // "apply to all rides" default behaviour from the WordPress plugin.

    function addRide() {
        // Snapshot ride 1's passenger fields as the default for the new ride.
        // Other ride fields (route, time, vehicle) start fresh.
        const ride1 = rides[0] || EMPTY_RIDE;
        const newRide = {
            ...EMPTY_RIDE,
            pax_name: ride1.pax_name || '',
            pax_phone: ride1.pax_phone || '',
            pax_count: ride1.pax_count || 1,
        };
        setRides((rs) => [...rs, newRide]);
        setPricings((ps) => [...ps, EMPTY_PRICING]);
        setLiveBillings((bs) => [...bs, { billedHours: 2, miles: null }]);
        setActiveRideIdx(rides.length);   // index of newly-added ride
    }
    
       function duplicateRide(idx) {
        const source = rides[idx];
        const sourcePricing = pricings[idx];
        const sourceBilling = liveBillings[idx];
        setRides((rs) => {
            const copy = JSON.parse(JSON.stringify(source));
            const next = [...rs];
            next.splice(idx + 1, 0, copy);
            return next;
        });
                setPricings((ps) => {
            const copyPricing = JSON.parse(JSON.stringify(sourcePricing));
            copyPricing.manually_overridden = true;
            const next = [...ps];
            next.splice(idx + 1, 0, copyPricing);
            return next;
        });
        setLiveBillings((bs) => {
            const next = [...bs];
            next.splice(idx + 1, 0, { ...sourceBilling });
            return next;
        });
        setActiveRideIdx(-1);
    }

    function removeRide(idx) {
        if (rides.length <= 1) return;     // never remove the only ride
        setRides((rs) => rs.filter((_, i) => i !== idx));
        setPricings((ps) => ps.filter((_, i) => i !== idx));
        setLiveBillings((bs) => bs.filter((_, i) => i !== idx));
        // If we removed the active one, fall back to the previous ride.
        setActiveRideIdx((cur) => {
            if (cur === idx) return Math.max(0, idx - 1);
            if (cur > idx) return cur - 1;
            return cur;
        });
    }

    function updateRide(idx, updater) {
        setRides((rs) => rs.map((r, i) => i === idx
            ? (typeof updater === 'function' ? updater(r) : updater)
            : r));
    }

    function updatePricing(idx, updater) {
        setPricings((ps) => ps.map((p, i) => i === idx
            ? (typeof updater === 'function' ? updater(p) : updater)
            : p));
    }

    function updateLiveBilling(idx, value) {
        setLiveBillings((bs) => bs.map((b, i) => i === idx ? value : b));
    }

    // Sum of per-ride manual prices — the default for the master total
    // when rides.length > 1. Computed live so the summary stays accurate
    // as dispatcher edits per-ride prices.
    const sumOfRidePrices = useMemo(() => {
        return pricings.reduce((sum, p) => {
            const v = parseFloat(p.manual_price);
            return isNaN(v) ? sum : sum + v;
        }, 0);
    }, [pricings]);

// Auto-sync master total to the sum of per-ride prices for as long as
    // the dispatcher hasn't taken ownership. Sync triggers when:
    //   - first becoming multi-ride (sum > 0, total empty)
    //   - adding another ride (sum changes, dispatcher hasn't typed)
    //   - editing a ride's price (sum changes, dispatcher hasn't typed)
    //
    // Once the dispatcher edits the field manually OR clicks "Use sum",
    // masterTotalUserEdited goes true and the effect leaves the value alone.
    useEffect(() => {
        if (rides.length > 1 && !masterTotalUserEdited && sumOfRidePrices > 0) {
            const next = sumOfRidePrices.toFixed(2);
            // Don't trigger a re-render if the value would be identical.
            if (next !== masterTotal) {
                setMasterTotal(next);
            }
        }
        if (rides.length === 1) {
            setMasterTotal('');
            setMasterTotalUserEdited(false);   // reset for next time multi-ride is built
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rides.length, sumOfRidePrices, masterTotalUserEdited]);
// Per-ride live billing is computed inside the RideAndPricingSection
    // component (one instance per ride). State lives at the page level
    // (liveBillings array) so the submit handler and the basket-summary
    // logic can read it. The effects that compute it are inside the
    // section component, keyed off the ride index.

    // ---- Step-by-step section reveal ------------------------------------
    //
    // Sections appear one at a time. The user must click "Continue" on each
    // to reveal the next. Order: customer → booker → passenger → ride →
    // pricing → payment.
    //
    // `revealed` is the index of the highest-revealed section (0-based).
    // 0 = only customer is visible
    // 1 = customer + booker
    // 5 = all six sections visible
    //
    // `expanded` controls which one is actually open vs collapsed-to-summary.
    // Continue clicks BOTH advance the reveal AND collapse the current section.
    // Click on a collapsed line OR on a summary's Edit icon re-expands it,
    // without affecting the others.
    // Order: customer → booker → passenger → ride+pricing → payment.
    // Ride and pricing are merged into one step because changing the ride
    // always means recomputing the price — splitting them adds friction
    // for what's actually one decision.
// SECTIONS reflects the current mode's flow. Booking mode runs the full
    // resolver; quote mode skips customer-side and runs straight from ride
    // to "Email quote to". Each mode has its own SECTIONS array; isVisible()
    // and continueFrom() use whichever applies.
    const SECTIONS = mode === 'booking'
        ? ['customer', 'booker', 'passenger', 'ride_pricing', 'payment']
        : ['ride_pricing', 'email_quote'];
    const [revealed, setRevealed] = useState(0);   // index of highest-revealed section

    function continueFrom(sectionName) {
        // Collapse the current section and reveal the next one.
        const idx = SECTIONS.indexOf(sectionName);
        setExpanded((prev) => ({ ...prev, [sectionName]: false }));
        if (idx + 1 < SECTIONS.length) {
            setRevealed((r) => Math.max(r, idx + 1));
            const next = SECTIONS[idx + 1];
            setExpanded((prev) => ({ ...prev, [next]: true }));
        }
    }

    function isVisible(sectionName) {
        return SECTIONS.indexOf(sectionName) <= revealed;
    }

    // When the customer changes, reset booker + passenger — they're scoped
    // under the customer. Also rewind step-by-step reveal back to customer
    // (booker/passenger no longer apply to the new customer; they need to
    // be re-resolved).
    function resetBookerAndPassenger() {
        setBooker({ mode: 'pick', selected: null, draft: EMPTY_BOOKER_DRAFT });
        setPassenger({ mode: 'same_as_booker', selected: null, draft: EMPTY_PASSENGER_DRAFT });
        setExpanded((prev) => ({ ...prev, booker: false, passenger: false }));
        setRevealed(0);
    }

    // -----------------------------------------------------------------------
    // Slice 7d.4 — Convert-from-quote prefill
    // -----------------------------------------------------------------------
    //
    // On mount in booking mode with ?quote_token=XXX, fetch the quote and
    // prefill rides + pricings + customer side. Smart customer prefill:
    //   - if quote.customer_id is set → fetch the customer and set
    //     customer.mode = 'pick' with that row pre-selected
    //   - else → customer.mode = 'create' with email + name from quote
    //
    // Doesn't run in quote mode (you'd be editing a sent quote, which we
    // don't support — quotes are immutable once sent). Doesn't fire if the
    // user has already started typing (we check for empty state to avoid
    // clobbering work-in-progress).
    useEffect(() => {
        const token = searchParams.get('quote_token');
        if (!token || mode !== 'booking') return;
        if (convertingFromQuote || quoteLoadError) return;   // already loaded / errored

        let cancelled = false;
        (async () => {
            try {
                const data = await api.get(`/q/${encodeURIComponent(token)}`);
                if (cancelled) return;
                applyQuotePrefill(data);
                setConvertingFromQuote({
                    // Public endpoint doesn't expose the internal UUID by
                    // design — token is the credential. Store the token here
                    // and pass it through on submit; backend resolves it.
                    quote_token: token,
                    quote_ref: data.quote_ref,
                });
            } catch (err) {
                if (cancelled) return;
                // Backend returns 410 with { code, quote_ref, message } for
                // expired/cancelled. useApi throws ApiError with .response.
                const resp = err?.response;
                setQuoteLoadError({
                    code: resp?.code || 'fetch_failed',
                    quote_ref: resp?.quote_ref || null,
                    message: resp?.message || err?.message || 'Could not load quote',
                });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, mode]);

    async function applyQuotePrefill(quote) {
        // Rides — straight copy. Quote rides have the same shape we expect
        // (mode, addresses, times, vehicle_class, etc.).
        const newRides = (quote.rides || []).map((r) => ({
            ...EMPTY_RIDE,
            // Normalise mode — older quotes may have 'one_way' (underscore);
            // booking schema expects 'oneway' (no underscore).
            mode: (r.mode === 'one_way' ? 'oneway' : r.mode) || 'oneway',
            pickup_address: r.pickup_address || '',
            dropoff_address: r.dropoff_address || '',
            stops: Array.isArray(r.stops)
                ? r.stops.map((s) => ({
                    address: typeof s === 'string' ? s : (s.address || ''),
                    time: typeof s === 'object' && s.time ? s.time : '',
                    meta: null,
                }))
                : [],
            pickup_date: r.pickup_date || tomorrow(),
            pickup_time: r.pickup_time || '10:00',
            final_time: r.final_time || '10:00',
            vehicle_class: r.vehicle_class || 'business',
            pax_count: r.pax_count || 1,
            pax_comments: r.pax_comments || '',
            flight_number: r.flight_number || '',
            tail_number: r.tail_number || '',
            fbo_code: r.fbo_code || '',
            customer_reference: r.customer_reference || '',
            show_flight_fields: !!(r.flight_number || r.tail_number || r.fbo_code),
        }));

        // Pricings — restore the full quote object (pricings array on the
        // quote row mirrors what /pricing/calculate originally returned).
        // manual_price is the per-ride total, pre-formatted.
        const newPricings = (quote.pricings || []).map((p, i) => ({
            quote: p,
            manual_price: p?.total != null ? Number(p.total).toFixed(2) : '',
            is_calculating: false,
            calc_error: null,
            journey: null,   // quote doesn't store journey display strings; user re-runs Calculate to refresh if needed
        }));

        // Live billings — naive default. The hourly recalc effect inside
        // each RideCardInBasket will refine this once the ride card is opened
        // and Distance Matrix runs.
        const newLiveBillings = newRides.map((r) => ({
            billedHours: (r.mode === 'hourly' && r.hours) ? r.hours : 2,
            miles: null,
        }));

        setRides(newRides.length > 0 ? newRides : [EMPTY_RIDE]);
        setPricings(newPricings.length > 0 ? newPricings : [EMPTY_PRICING]);
        setLiveBillings(newLiveBillings.length > 0 ? newLiveBillings : [{ billedHours: 2, miles: null }]);

        // Master total — quote stores `master_total` for multi-ride.
        if (newRides.length > 1 && quote.master_total != null) {
            setMasterTotal(Number(quote.master_total).toFixed(2));
            setMasterTotalUserEdited(true);   // treat the quote's total as authoritative
        }

        // Customer prefill — smart split.
        if (quote.customer_id) {
            // Existing customer — fetch the full row so PaymentSection has
            // all the flags it needs (account_billing_enabled, has_payment_method,
            // default_payment_method_last4, etc.).
            try {
                const customerRow = await api.get(`/customers/${quote.customer_id}`);
                const c = customerRow?.customer || customerRow;
                if (c && c.id) {
                    setCustomer({
                        mode: 'pick',
                        selected: c,
                        draft: EMPTY_CUSTOMER_DRAFT,
                    });
                }
            } catch {
                // If the lookup fails, fall back to create-mode prefill so
                // dispatcher isn't stuck.
                setCustomer({
                    mode: 'create',
                    selected: null,
                    draft: {
                        ...EMPTY_CUSTOMER_DRAFT,
                        email: quote.customer_email || '',
                        first_name: (quote.customer_name || '').split(' ')[0] || '',
                        last_name: (quote.customer_name || '').split(' ').slice(1).join(' ') || '',
                    },
                });
            }
        } else {
            // No linked customer — create mode with email + best-effort name split.
            const name = quote.customer_name || '';
            const parts = name.trim().split(/\s+/);
            setCustomer({
                mode: 'create',
                selected: null,
                draft: {
                    ...EMPTY_CUSTOMER_DRAFT,
                    email: quote.customer_email || '',
                    first_name: parts[0] || '',
                    last_name: parts.slice(1).join(' ') || '',
                },
            });
        }

        // Booker prefill — if quote captured a booker_id, resolve and pick it.
        if (quote.booker_id && quote.customer_id) {
            try {
                const bookerData = await api.get(`/customers/${quote.customer_id}/bookers`);
                const found = (bookerData?.bookers || []).find((b) => b.id === quote.booker_id);
                if (found) {
                    setBooker({ mode: 'pick', selected: found, draft: EMPTY_BOOKER_DRAFT });
                }
            } catch {
                // FK was deleted or fetch failed — leave as default.
            }
        }

        // Passenger prefill — if quote captured a passenger_id, resolve and pick it.
        if (quote.passenger_id && quote.customer_id) {
            try {
                const pData = await api.get(`/customers/${quote.customer_id}/passengers`);
                const found = (pData?.passengers || []).find((p) => p.id === quote.passenger_id);
                if (found) {
                    setPassenger({ mode: 'pick', selected: found, draft: EMPTY_PASSENGER_DRAFT });
                }
            } catch {
                // Same fallback — leave as default.
            }
        }

        // Reveal the form — start with customer expanded since rides are
        // already filled in. Dispatcher resolves customer/booker/passenger,
        // then payment, and submits.
        setRevealed(SECTIONS.length - 1);   // unlock all sections
        setExpanded({
            customer: true,
            booker: false,
            passenger: false,
            ride_pricing: false,
            payment: false,
            email_quote: false,
        });
        setActiveRideIdx(-1);   // collapse all ride cards
    }

    // Quote-mode prefill: when switching INTO quote mode, prefill the
    // quote recipient from whatever booker/customer data has been entered.
    // Reduces typing for the "I started a booking, customer wants a quote
    // first" path. Doesn't fire when starting in quote mode from URL param —
    // there's nothing to prefill from. Stays a hint: dispatcher can edit.
    useEffect(() => {
        if (mode !== 'quote') return;
        if (quoteRecipient.email) return;   // already typed something — don't clobber

        const bookerEmail = booker.selected?.email || booker.draft?.email || '';
        const bookerName = booker.selected?.name || booker.draft?.name || '';
        const customerEmail = customer.selected?.email || customer.draft?.email || '';
        const customerFirstName = customer.selected?.first_name || customer.draft?.first_name || '';
        const customerLastName = customer.selected?.last_name || customer.draft?.last_name || '';
        const customerName = `${customerFirstName} ${customerLastName}`.trim();

        const email = bookerEmail || customerEmail;
        const name = bookerName || customerName;

        if (email || name) {
            setQuoteRecipient((prev) => ({
                ...prev,
                email: prev.email || email,
                name: prev.name || name,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    // When customer is finalised (either picked an existing one OR completed
    // a new draft), prefill the booker draft from customer contact details
    // and force booker mode to 'create' for new customers (no existing
    // bookers to pick under a customer that doesn't exist yet).
    useEffect(() => {
        const customerSettled = customer.mode === 'pick'
            ? !!customer.selected
            : isCustomerComplete(customer);

        if (!customerSettled) return;

        // For existing customers — default to pick mode so dispatcher can
        // pick from any existing bookers on file. The BookerPicker handles
        // the "no bookers yet" case with a friendly fallback message.
        // For new customers — there are no existing bookers, so default to
        // create mode + prefill from the customer draft (their primary
        // contact is usually also the first booker).
        if (customer.mode === 'pick') {
            setBooker((prev) => {
                // Don't overwrite if dispatcher has already picked or typed.
                if (prev.selected) return prev;
                const draftHasInput = prev.draft.name || prev.draft.email || prev.draft.phone;
                if (draftHasInput) return prev;
                return {
                    mode: 'pick',
                    selected: null,
                    draft: EMPTY_BOOKER_DRAFT,
                };
            });
            return;
        }

        // mode === 'create': prefill from customer draft.
        const c = customer.draft;
        const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();

        setBooker((prev) => {
            if (prev.selected) return prev;
            const draftHasInput = prev.draft.name || prev.draft.email || prev.draft.phone;
            if (draftHasInput) return prev;
            return {
                mode: 'create',
                selected: null,
                draft: {
                    name: name || '',
                    email: c.email || '',
                    phone: c.phone || '',
                },
            };
        });
    }, [
        customer.mode,
        customer.selected?.id,
        // For create mode — re-run when the email becomes valid (which is
        // the moment isCustomerComplete flips true).
        customer.draft.email,
        customer.draft.first_name,
        customer.draft.last_name,
    ]);

    // When mode flips, the first section of that mode must be the one
    // that's expanded. Otherwise: in booking mode the Customer section is
    // expanded by default (state init); switching to quote means ride_pricing
    // should be the first expanded section instead.
    useEffect(() => {
        if (mode === 'quote') {
            setExpanded((prev) => ({
                ...prev,
                ride_pricing: true,
                customer: false,
                booker: false,
                passenger: false,
                payment: false,
                email_quote: false,
            }));
        } else {
            setExpanded((prev) => ({
                ...prev,
                customer: true,
                ride_pricing: false,
                email_quote: false,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    // -----------------------------------------------------------------------
    // Submit handler
    // -----------------------------------------------------------------------
    //
    // Builds the request body matching backend manualBookingSchema:
    //   - customer_id OR customer object
    //   - booker_id OR booker object
    //   - passenger_id, passenger object, OR passenger_same_as_booker=true
    //   - rides: [single ride with quoted_price, price_breakdown, override_reason]
    //   - payment_method, payment_external_note (if external)
    //   - internal_notes
    //
    // Redirects to /bookings/:id on success. Surfaces ApiError message on
    // failure (Zod validation messages will land here).
async function handleSubmit() {
        setSubmitError(null);
        setSubmitting(true);

       try {
            const body = buildSubmitPayload({
                customer, booker, passenger,
                rides, pricings, liveBillings, masterTotal,
                payment,
                fromQuoteToken: convertingFromQuote?.quote_token || null,
            });
            console.log('[DEBUG] convertingFromQuote:', convertingFromQuote);
            console.log('[DEBUG] body.from_quote_id:', body.from_quote_id);
       const result = await api.post('/bookings/manual', body);

            // Save ticked addresses / booking notes to the customer for next time.
            // Best-effort: a failure here never blocks the booking.
            try {
                const customerId = result?.bookings?.[0]?.customer_id || customer.selected?.id;
                if (customerId) {
                    const norm = (s) => (s || '').trim().toLowerCase();
                    const knownAddresses = new Set((customer.selected?.addresses || []).map((a) => norm(a.address)));
                    const knownNotes = new Set((customer.selected?.saved_notes || []).map((n) => norm(n.note_text)));
                    let savedCount = 0;

                    for (const r of rides) {
                        const addressTicks = [
                            [r.save_pickup, r.pickup_address, r.pickup_address_meta, 'Pickup'],
                            [r.save_dropoff, r.dropoff_address, r.dropoff_address_meta, 'Drop-off'],
                        ];
                        for (const [tick, addr, meta, fallbackLabel] of addressTicks) {
                            const a = (addr || '').trim();
                            if (!tick?.on || !a || knownAddresses.has(norm(a))) continue;
                            knownAddresses.add(norm(a));
                            await api.post(`/customers/${customerId}/addresses`, {
                                label: (tick.label || '').trim() || fallbackLabel,
                                address: a,
                                ...(meta?.postcode ? { postcode: meta.postcode } : {}),
                                ...(meta?.latitude != null ? { latitude: Number(meta.latitude) } : {}),
                                ...(meta?.longitude != null ? { longitude: Number(meta.longitude) } : {}),
                            });
                            savedCount++;
                        }

                        const note = (r.pax_comments || '').trim();
                        if (r.save_note && note && !knownNotes.has(norm(note))) {
                            knownNotes.add(norm(note));
                            await api.post(`/customers/${customerId}/saved-notes`, { label: '', note_text: note });
                            savedCount++;
                        }
                    }

                    if (savedCount > 0) {
                        toast.success(`Saved ${savedCount} item${savedCount === 1 ? '' : 's'} to the customer for next time`);
                    }
                }
            } catch (saveErr) {
                toast.error(`Booking created, but saving to the customer failed: ${saveErr?.message || 'unknown error'}`);
            }
            // If we converted from a quote, invalidate the quote cache so
            // when the dispatcher navigates back to it (or to the quotes
            // list), they see the fresh 'converted' status without needing
            // to manually refresh the page.
            if (convertingFromQuote?.quote_token) {
                queryClient.invalidateQueries({ queryKey: ['quotes'] });
            }
            // Backend returns { master_ref, bookings: [...], count }.
            // Navigate to the first ride's detail page; if multi-ride, the
            // ItineraryCard there links to the siblings.
            navigate(`/bookings/${result.bookings[0].id}`);
        } catch (err) {
            const msg = err?.response?.message || err?.message || 'Something went wrong creating the booking.';
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    }

    // -----------------------------------------------------------------------
    // Quote submit handler
    // -----------------------------------------------------------------------
    //
    // Builds the request body for POST /quotes. Two actions:
    //   - 'send'  — create + immediately email the quote
    //   - 'draft' — create only, dispatcher will send later from /quotes/:id
    //
    // Same rides+pricings shape as the booking submit. Customer email/name
    // come from the quote-mode "Email quote to" section. Validity in days is
    // configurable; backend computes expires_at server-side.
    //
    // Redirects to /quotes/:id on success once that page exists. For now,
    // toasts the quote_ref and stays on the page so the dispatcher can
    // start a new quote without page-flicker.
async function handleSubmitQuote(action) {
        setSubmitError(null);
        setSubmitting(true);

try {
                        const body = buildQuoteSubmitPayload({
                liveBillings,
                rides, pricings, masterTotal,
                quoteRecipient,
                // Create as draft regardless of action — we'll fire /send
                // from inside the preview modal for the 'send' case.
                action: 'draft',
            });
            const result = await api.post('/quotes', body);
            const ref = result?.quote?.quote_ref || 'created';
            const newQuote = result?.quote;

            if (action === 'draft') {
                toast.success(`Quote ${ref} saved as draft`);
                navigate(newQuote?.id ? `/quotes/${newQuote.id}` : '/quotes');
                return;
            }

            // action === 'send' → open preview modal, send fires from there.
            if (newQuote?.id) {
                setPreviewQuote(newQuote);
            } else {
                // Defensive — shouldn't happen, but don't strand the dispatcher.
                navigate('/quotes');
            }
        } catch (err) {
            const msg = err?.response?.message || err?.message || 'Something went wrong creating the quote.';
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    }

    // Pattern 2 — "Email as quote" from booking mode mid-form. Doesn't reset
    // booking state; just opens a modal that captures recipient + sends.
async function handleEmailAsQuote({ email, name, validity_days, notes, action }) {
        try {
                        const body = buildQuoteSubmitPayload({
                liveBillings,
                rides, pricings, masterTotal,
                                quoteRecipient: { email, name, validity_days, notes },
                // Same as the main flow — always create as draft first.
                action: 'draft',
            });
            if (booker.mode === 'pick' && booker.selected) {
                body.booker_id = booker.selected.id;
            }
            if (passenger.mode === 'pick' && passenger.selected) {
                body.passenger_id = passenger.selected.id;
            }
            const result = await api.post('/quotes', body);
            const ref = result?.quote?.quote_ref || 'created';
            const newQuote = result?.quote;
            setEmailQuoteModalOpen(false);

            if (action === 'draft') {
                toast.success(`Quote ${ref} saved as draft`);
                navigate(newQuote?.id ? `/quotes/${newQuote.id}` : '/quotes');
                return;
            }

            if (newQuote?.id) {
                setPreviewQuote(newQuote);
            } else {
                navigate('/quotes');
            }
        } catch (err) {
            const msg = err?.response?.message || err?.message || 'Something went wrong sending the quote.';
            toast.error(msg);
            throw err;
        }
    }

    // Owner-only / admin-only gate. Match the same pattern used elsewhere.
    if (!isAdmin) {
        return (
            <div className="p-6 max-w-2xl mx-auto text-sm text-ink-subtle">
                You don't have permission to create bookings.
            </div>
        );
    }

// Section numbering — booking mode shows 1-5, quote mode shows 1-2.
    // Computed from mode rather than hard-coded so renumbering a step is
    // a one-place change.
    const sectionNumber = (sectionName) => {
        const idx = SECTIONS.indexOf(sectionName);
        return idx >= 0 ? String(idx + 1) : '?';
    };

    return (
        <div className="max-w-3xl mx-auto pb-20">
            <Link
                to="/bookings"
                className="inline-flex items-center gap-1.5 text-sm text-ink-subtle hover:text-ink mb-3"
            >
                <ArrowLeft className="w-4 h-4" />
                Back to bookings
            </Link>

            <PageHeader
                title={mode === 'quote' ? 'New quote' : 'New booking'}
                subtitle={mode === 'quote'
                    ? "Quote a ride without taking the booking — customer gets it by email."
                    : "Take a booking over the phone — customer, ride, payment, done."}
            />

         {/* Convert-from-quote banner — visible during conversion flow */}
            {convertingFromQuote && !quoteLoadError && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-start gap-2">
                    <FileText size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                        <div className="font-semibold text-blue-900">
                            Converting quote {convertingFromQuote.quote_ref} to booking
                        </div>
                        <div className="text-blue-800 mt-0.5">
                            Ride and pricing details have been prefilled. Complete the customer, booker, passenger, and payment to confirm.
                        </div>
                    </div>
                </div>
            )}

            {previewQuote && (
                <EmailPreviewModal
                    emails={[{
                        id: 'quote',
                        label: 'Send quote',
                        previewParams: { kind: 'quote', quote_id: previewQuote.id },
                        sendEndpoint: `/quotes/${previewQuote.id}/send`,
                        sendBody: (recipients) => ({
                            recipients,
                            customer_email: previewQuote.customer_email,
                            customer_name: previewQuote.customer_name || null,
                        }),
                    }]}
                    title="Send quote"
                    description={`Quote ${previewQuote.quote_ref} \u2014 review before sending.`}
                    onClose={() => {
                        const id = previewQuote.id;
                        setPreviewQuote(null);
                        navigate(`/quotes/${id}`);
                    }}
                    onSent={() => {
                        const id = previewQuote.id;
                        setPreviewQuote(null);
                        queryClient.invalidateQueries({ queryKey: ['quotes'] });
                        navigate(`/quotes/${id}`);
                    }}
                />
            )}

            
            {quoteLoadError && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                    <AlertCircle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                        <div className="font-semibold text-amber-900">
                            Quote {quoteLoadError.quote_ref || ''} couldn&apos;t be loaded
                        </div>
                        <div className="text-amber-800 mt-0.5">
                            {quoteLoadError.message}. You can carry on with a fresh booking.
                        </div>
                    </div>
                </div>
            )}
            {/* Mode toggle — Booking vs Quote. Defaults to Booking; flips
                to Quote either via this toggle or via ?mode=quote in the URL.
                Switching modes preserves ride+pricing state. */}
            <div className="mt-4 flex gap-1 p-1 bg-canvas rounded-lg w-fit">
                {[
                    { value: 'booking', label: 'Booking', Icon: Calendar },
                    { value: 'quote',   label: 'Quote',   Icon: FileText },
                ].map(({ value, label, Icon }) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => switchMode(value)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                            mode === value
                                ? 'bg-paper text-ink shadow-sm'
                                : 'text-ink-subtle hover:text-ink'
                        }`}
                    >
                        <Icon className="w-4 h-4" />
                        {label}
                    </button>
                ))}
            </div>

            <div className="space-y-6 mt-6">

                {/* ===== BOOKING MODE — full customer-side flow ===== */}
                {mode === 'booking' && isVisible('customer') && (
                    <CustomerSection
                        state={customer}
                        setState={setCustomer}
                        onCustomerChange={resetBookerAndPassenger}
                        collapsed={!expanded.customer}
                        onExpand={() => expandOnly('customer')}
                        onCollapse={() => continueFrom('customer')}
                        sectionNumber={sectionNumber('customer')}
                    />
                )}

                {mode === 'booking' && isVisible('booker') && (customer.selected || customer.mode === 'create') && (
                    <BookerSection
                        customer={customer}
                        state={booker}
                        setState={setBooker}
                        collapsed={!expanded.booker}
                        onExpand={() => expandOnly('booker')}
                        onCollapse={() => continueFrom('booker')}
                        sectionNumber={sectionNumber('booker')}
                    />
                )}

                {mode === 'booking' && isVisible('passenger') && (customer.selected || customer.mode === 'create') && (
                    <PassengerSection
                        customer={customer}
                        booker={booker}
                        state={passenger}
                        setState={setPassenger}
                        collapsed={!expanded.passenger}
                        onExpand={() => expandOnly('passenger')}
                        onCollapse={() => continueFrom('passenger')}
                        sectionNumber={sectionNumber('passenger')}
                    />
                )}

                {/* ===== SHARED — ride+pricing in both modes ===== */}
              {isVisible('ride_pricing') && (
                    <RidesBasket
                        rides={rides}
                        pricings={pricings}
                        liveBillings={liveBillings}
                        masterTotal={masterTotal}
                        setMasterTotal={setMasterTotal}
                        setMasterTotalUserEdited={setMasterTotalUserEdited}
                        sumOfRidePrices={sumOfRidePrices}
                        activeRideIdx={activeRideIdx}
                        setActiveRideIdx={setActiveRideIdx}
                        addRide={addRide}
                        duplicateRide={duplicateRide}
                        removeRide={removeRide}
                        updateRide={updateRide}
                        updatePricing={updatePricing}
                       updateLiveBilling={updateLiveBilling}
                        savedAddresses={savedAddresses}
                        savedNotes={savedNotes}
                        collapsed={!expanded.ride_pricing}
                       onExpand={() => expandOnly('ride_pricing')}
                        onCollapse={() => continueFrom('ride_pricing')}
                        sectionNumber={sectionNumber('ride_pricing')}
                        customerId={customer.selected?.id || null}
                    />
                )}

                {/* ===== BOOKING MODE — payment + submit ===== */}
                {mode === 'booking' && isVisible('payment') && (
                    <PaymentSection
                        customer={customer}
                        booker={booker}
                        payment={payment}
                        setPayment={setPayment}
                        collapsed={!expanded.payment}
                        onExpand={() => expandOnly('payment')}
                        onCollapse={() => continueFrom('payment')}
                        sectionNumber={sectionNumber('payment')}
                    />
                )}

                {mode === 'booking' && isVisible('payment') && !expanded.payment && (
                    <SubmitBlock
                        canSubmit={canSubmit({
                            customer, booker, passenger,
                            rides, pricings, masterTotal,
                            payment,
                        })}
                        submitting={submitting}
                        submitError={submitError}
                        onSubmit={handleSubmit}
                        // Hide "Email as quote" when we're already converting
                        // from a quote — the user is committing to a booking,
                        // they don't need an escape back to quote-land.
                        onEmailAsQuote={convertingFromQuote
                            ? null
                            : () => setEmailQuoteModalOpen(true)}
                    />
                )}

                {/* ===== QUOTE MODE — email recipient + submit ===== */}
                {mode === 'quote' && isVisible('email_quote') && (
                    <EmailQuoteSection
                        recipient={quoteRecipient}
                        setRecipient={setQuoteRecipient}
                        collapsed={!expanded.email_quote}
                        onExpand={() => expandOnly('email_quote')}
                        onCollapse={() => continueFrom('email_quote')}
                        sectionNumber={sectionNumber('email_quote')}
                    />
                )}

                {mode === 'quote' && isVisible('email_quote') && !expanded.email_quote && (
                    <SubmitQuoteBlock
                        canSubmit={canSubmitQuote({ rides, pricings, masterTotal, quoteRecipient })}
                        submitting={submitting}
                        submitError={submitError}
                        onSaveDraft={() => handleSubmitQuote('draft')}
                        onSendQuote={() => handleSubmitQuote('send')}
                    />
                )}
            </div>

            {/* "Email as quote" modal — fires from booking mode mid-form */}
            {emailQuoteModalOpen && (
                <EmailAsQuoteModal
                    rides={rides}
                    pricings={pricings}
                    masterTotal={masterTotal}
                    initialEmail={booker.selected?.email || booker.draft?.email || ''}
                    initialName={booker.selected?.name || booker.draft?.name || ''}
                    onCancel={() => setEmailQuoteModalOpen(false)}
                    onSend={handleEmailAsQuote}
                />
            )}
        </div>
    );
}


// =============================================================================
// CustomerSection
// =============================================================================
//
// Two modes: pick (search existing) and create (new customer form).
// Mode toggle is the section header. Email-blur detection in create mode
// surfaces existing matches as a one-click "use existing" prompt.
// =============================================================================

function CustomerSection({ state, setState, onCustomerChange, collapsed, onExpand, onCollapse, sectionNumber }) {
    const api = useApi();

    function setMode(mode) {
        setState({
            mode,
            selected: null,
            draft: EMPTY_CUSTOMER_DRAFT,
        });
        onCustomerChange();
    }

    async function setSelected(c) {
        // Optimistic — set the lean row immediately so the UI feels snappy
        // and downstream sections (Booker, Passenger) start loading.
        setState((s) => ({ ...s, selected: c }));
        onCustomerChange();

        // Then fetch the full customer record (includes addresses, payment
        // methods flags, account_billing_enabled, etc.) and merge in.
        // GET /customers (the search list) returns lean rows — only the
        // detail endpoint hydrates the addresses array used by the
        // saved-addresses picker on AddressInput.
        try {
            const data = await api.get(`/customers/${c.id}`);
            const full = data?.customer || data;
            if (full && full.id === c.id) {
                setState((s) => {
                    // Only overwrite if the user hasn't picked a different
                    // customer in the meantime — race protection.
                    if (s.selected?.id !== c.id) return s;
                    return { ...s, selected: { ...c, ...full } };
                });
            }
        } catch {
            // Silent failure — picker just won't show saved addresses.
            // Lean row still works for everything else.
        }
    }

    function setDraft(updater) {
        setState((s) => ({ ...s, draft: typeof updater === 'function' ? updater(s.draft) : updater }));
    }

    function clearSelection() {
        setState({ mode: 'pick', selected: null, draft: EMPTY_CUSTOMER_DRAFT });
        onCustomerChange();
    }

    return (
        <SectionCard
            title="Customer"
            number="1"
            sectionNumber={sectionNumber}
            collapsed={collapsed}
            summary={customerSummary(state)}
            onExpand={onExpand}
            canCollapse={isCustomerComplete(state)}
            onCollapse={onCollapse}
            tabs={[
                { value: 'pick', label: 'Search existing' },
                { value: 'create', label: 'New customer' },
            ]}
            activeTab={state.mode}
            onTabChange={setMode}
        >
            {state.mode === 'pick' && (
                <CustomerPicker
                    selected={state.selected}
                    onSelect={setSelected}
                    onClear={clearSelection}
                />
            )}

            {state.mode === 'create' && (
                <CustomerCreateForm
                    draft={state.draft}
                    onChange={setDraft}
                    onUseExisting={(c) => {
                        // Switch to pick mode with that customer resolved.
                        setState({ mode: 'pick', selected: c, draft: EMPTY_CUSTOMER_DRAFT });
                        onCustomerChange();
                    }}
                />
            )}
        </SectionCard>
    );
}


function CustomerPicker({ selected, onSelect, onClear }) {
    const api = useApi();
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 200);
        return () => clearTimeout(t);
    }, [query]);

    const { data, isFetching } = useQuery({
        queryKey: ['customers', 'search', debounced],
        queryFn: () => api.get('/customers', {
            query: {
                search: debounced || undefined,
                status: 'active',
                limit: 10,
            },
        }),
        enabled: debounced.length > 0 && !selected,
    });

    const results = data?.customers || [];

    if (selected) {
        return (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-paper-border bg-canvas">
                <div className="flex items-center gap-2 min-w-0">
                    {selected.customer_type === 'company' ? (
                        <Building2 className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                    ) : (
                        <UserCircle className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-ink truncate">
                            {customerDisplayName(selected)}
                        </div>
                        <div className="text-xs text-ink-subtle truncate">
                            {selected.email}{selected.phone ? ` · ${selected.phone}` : ''}
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    className="text-xs text-ink-subtle hover:text-ink underline"
                >
                    Change
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, company, or email…"
                    className="w-full text-sm pl-8 pr-3 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    autoFocus
                />
            </div>

            {debounced.length > 0 && (
                <div className="mt-2 border border-paper-border rounded-lg overflow-hidden">
                    {isFetching && (
                        <div className="px-3 py-2 text-sm text-ink-subtle">Searching…</div>
                    )}
                    {!isFetching && results.length === 0 && (
                        <div className="px-3 py-3 text-sm text-ink-subtle">
                            No matching customers. Switch to "New customer" tab to create one.
                        </div>
                    )}
                    {results.map((c) => {
    const contactPerson = customerContactPerson(c);
    return (
        <button
            key={c.id}
            type="button"
            onClick={() => onSelect(c)}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-canvas text-left border-b border-paper-border-soft last:border-0"
        >
            {c.company_name ? (
                <Building2 className="h-4 w-4 text-ink-subtle flex-shrink-0" />
            ) : (
                <UserCircle className="h-4 w-4 text-ink-subtle flex-shrink-0" />
            )}
            <div className="min-w-0">
                <div className="text-sm font-medium text-ink truncate">
                    {customerDisplayName(c)}
                </div>
                {contactPerson && (
                    <div className="text-xs text-ink-subtle truncate">
                        Contact: {contactPerson}
                    </div>
                )}
                <div className="text-xs text-ink-subtle truncate">
                    {c.email}{c.phone ? ` · ${c.phone}` : ''}
                </div>
            </div>
        </button>
    );
})}
                </div>
            )}
        </div>
    );
}


function CustomerCreateForm({ draft, onChange, onUseExisting }) {
    const api = useApi();
    const [emailMatch, setEmailMatch] = useState(null);  // matched customer or null
    const [emailChecking, setEmailChecking] = useState(false);

    function update(field, value) {
        onChange((d) => ({ ...d, [field]: value }));
    }

    async function checkEmailMatch(email) {
        const trimmed = (email || '').trim().toLowerCase();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setEmailMatch(null);
            return;
        }
        setEmailChecking(true);
        try {
            const data = await api.get('/customers', {
                query: { search: trimmed, status: 'all', limit: 5 },
            });
            const customers = data?.customers || [];
            const exact = customers.find(
                (c) => (c.email || '').toLowerCase() === trimmed
            );
            setEmailMatch(exact || null);
        } catch {
            setEmailMatch(null);
        } finally {
            setEmailChecking(false);
        }
    }

    return (
        <div className="space-y-3">
            <FieldLabel label="Company name">
    <input
        type="text"
        value={draft.company_name}
        onChange={(e) => update('company_name', e.target.value)}
        placeholder="Optional — leave empty for private customers"
        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
    />
</FieldLabel>

<FieldLabel label="Customer type">
    <input
        type="text"
        list="customer-type-suggestions"
        value={draft.customer_type}
        onChange={(e) => update('customer_type', e.target.value)}
        placeholder="Individual, Corporate, Hotel…"
        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
    />
    <datalist id="customer-type-suggestions">
        <option value="Individual" />
        <option value="Corporate" />
        <option value="Chauffeur partner" />
        <option value="Hotel" />
        <option value="Travel agency" />
        <option value="Event organiser" />
    </datalist>
</FieldLabel>

            <div className="grid grid-cols-2 gap-3">
                <FieldLabel label="First name" required>
                    <input
                        type="text"
                        value={draft.first_name}
                        onChange={(e) => update('first_name', e.target.value)}
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>
                <FieldLabel label="Last name" required>
                    <input
                        type="text"
                        value={draft.last_name}
                        onChange={(e) => update('last_name', e.target.value)}
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>
            </div>

            <FieldLabel label="Email" required>
                <input
                    type="email"
                    value={draft.email}
                    onChange={(e) => update('email', e.target.value)}
                    onBlur={(e) => checkEmailMatch(e.target.value)}
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>

            {/* Email match — surfaces an existing customer with this email and
                offers a one-click "use existing" path. */}
            {emailMatch && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm">
                    <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-amber-900">
                            A customer already exists with this email
                        </div>
                        <div className="text-xs text-amber-800 mt-0.5 truncate">
                            {customerDisplayName(emailMatch)} · {emailMatch.email}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onUseExisting(emailMatch)}
                        className="text-xs font-medium text-amber-900 hover:underline whitespace-nowrap"
                    >
                        Use existing →
                    </button>
                </div>
            )}

            <FieldLabel label="Phone">
                <input
                    type="tel"
                    value={draft.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="07000 000000"
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>
        </div>
    );
}


// =============================================================================
// BookerSection
// =============================================================================

function BookerSection({ customer, state, setState, collapsed, onExpand, onCollapse, sectionNumber }) {
    function setMode(mode) {
        setState({ mode, selected: null, draft: EMPTY_BOOKER_DRAFT });
    }

    function setSelected(b) {
        setState((s) => ({ ...s, selected: b }));
    }

    function setDraft(updater) {
        setState((s) => ({ ...s, draft: typeof updater === 'function' ? updater(s.draft) : updater }));
    }

    // Helper: pre-fill the booker draft from the customer's primary contact.
    // Only useful when creating a new booker — when picking from existing
    // bookers, this button does nothing meaningful, so we hide it.
    function useCustomerPrimary() {
        const c = customer.selected || customer.draft;
        const name = `${c.first_name} ${c.last_name}`.trim();
        setState({
            mode: 'create',
            selected: null,
            draft: {
                name: name || '',
                email: c.email || '',
                phone: c.phone || '',
            },
        });
    }

    // Existing customer is required for "pick" mode (we need a customer_id
    // to scope the search). Brand-new customers in the create flow can only
    // do "create new booker" — we can't pick a booker that doesn't exist
    // under a customer that doesn't exist.
    const canPickExisting = !!customer.selected;

    return (
        <SectionCard
            title="Booker"
            number="2"
            sectionNumber={sectionNumber}
            subtitle="The person making the booking. Receives the booking confirmation."
            collapsed={collapsed}
            summary={bookerSummary(state)}
            onExpand={onExpand}
            canCollapse={isBookerComplete(state)}
            onCollapse={onCollapse}
            tabs={canPickExisting ? [
                { value: 'pick', label: 'Existing booker' },
                { value: 'create', label: 'New booker' },
            ] : null}
            activeTab={state.mode}
            onTabChange={setMode}
            extraHeaderAction={
                // Only show "Use customer primary contact" in create mode.
                // Doesn't make sense when picking an existing booker.
                state.mode === 'create' ? (
                    <button
                        type="button"
                        onClick={useCustomerPrimary}
                        className="text-xs text-ink-subtle hover:text-ink underline"
                    >
                        Use customer primary contact
                    </button>
                ) : null
            }
        >
            {canPickExisting && state.mode === 'pick' && (
                <BookerPicker
                    customerId={customer.selected.id}
                    selected={state.selected}
                    onSelect={setSelected}
                    onClear={() => setState((s) => ({ ...s, selected: null }))}
                />
            )}

            {(state.mode === 'create' || !canPickExisting) && (
                <BookerCreateForm draft={state.draft} onChange={setDraft} />
            )}
        </SectionCard>
    );
}


function BookerPicker({ customerId, selected, onSelect, onClear }) {
    const api = useApi();

    const { data, isLoading } = useQuery({
        queryKey: ['customer', customerId, 'bookers'],
        queryFn: () => api.get(`/customers/${customerId}/bookers`),
        enabled: !!customerId,
    });

    const bookers = data?.bookers || [];

    if (selected) {
    const contactPerson = customerContactPerson(selected);
    return (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-paper-border bg-canvas">
            <div className="flex items-center gap-2 min-w-0">
                {selected.company_name ? (
                    <Building2 className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                ) : (
                    <UserCircle className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                )}
                <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">
                        {customerDisplayName(selected)}
                    </div>
                    {contactPerson && (
                        <div className="text-xs text-ink-subtle truncate">
                            Contact: {contactPerson}
                        </div>
                    )}
                    <div className="text-xs text-ink-subtle truncate">
                        {selected.email}{selected.phone ? ` · ${selected.phone}` : ''}
                    </div>
                </div>
            </div>
            <button
                type="button"
                onClick={onClear}
                className="text-xs text-ink-subtle hover:text-ink underline"
            >
                Change
            </button>
        </div>
    );
}

    if (isLoading) {
        return <div className="text-sm text-ink-subtle">Loading bookers…</div>;
    }

    if (bookers.length === 0) {
        return (
            <div className="p-3 rounded-lg border border-paper-border-soft bg-canvas text-sm text-ink-subtle">
                No bookers yet for this customer. Switch to "New booker" to add one.
            </div>
        );
    }

    return (
        <div className="border border-paper-border rounded-lg overflow-hidden">
            {bookers.map((b) => (
                <button
                    key={b.id}
                    type="button"
                    onClick={() => onSelect(b)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-canvas text-left border-b border-paper-border-soft last:border-0"
                >
                    <UserIcon className="h-4 w-4 text-ink-subtle flex-shrink-0" />
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-ink truncate">{b.name}</div>
                        <div className="text-xs text-ink-subtle truncate">
                            {b.email}{b.phone ? ` · ${b.phone}` : ''}
                        </div>
                    </div>
                </button>
            ))}
        </div>
    );
}


function BookerCreateForm({ draft, onChange }) {
    function update(field, value) {
        onChange((d) => ({ ...d, [field]: value }));
    }

    return (
        <div className="space-y-3">
            <FieldLabel label="Name" required>
                <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => update('name', e.target.value)}
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>
            <FieldLabel label="Email" required>
                <input
                    type="email"
                    value={draft.email}
                    onChange={(e) => update('email', e.target.value)}
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>
            <FieldLabel label="Phone">
                <input
                    type="tel"
                    value={draft.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="07000 000000"
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>
        </div>
    );
}


// =============================================================================
// PassengerSection
// =============================================================================

function PassengerSection({ customer, booker, state, setState, collapsed, onExpand, onCollapse, sectionNumber }) {
    function setMode(mode) {
        setState({ mode, selected: null, draft: EMPTY_PASSENGER_DRAFT });
    }

    function setSelected(p) {
        setState((s) => ({ ...s, selected: p }));
    }

    function setDraft(updater) {
        setState((s) => ({ ...s, draft: typeof updater === 'function' ? updater(s.draft) : updater }));
    }

    const canPickExisting = !!customer.selected;

    const tabs = [
        { value: 'same_as_booker', label: "I'm the passenger" },
        ...(canPickExisting ? [{ value: 'pick', label: 'Existing passenger' }] : []),
        { value: 'create', label: 'New passenger' },
    ];

    return (
       <SectionCard
            title="Passenger"
            number="3"
            sectionNumber={sectionNumber}
            subtitle="Who's actually travelling. Often the same person as the booker."
            collapsed={collapsed}
            summary={passengerSummary(state, booker)}
            onExpand={onExpand}
            canCollapse={isPassengerComplete(state)}
            onCollapse={onCollapse}
            tabs={tabs}
            activeTab={state.mode}
            onTabChange={setMode}
        >
            {state.mode === 'same_as_booker' && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-paper-border-soft bg-canvas text-sm text-ink-subtle">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    <span>The booker will also be recorded as the passenger for this booking.</span>
                </div>
            )}

                     {state.mode === 'pick' && canPickExisting && (
                <PassengerPicker
                    customerId={customer.selected.id}
                    selected={state.selected}
                    onSelect={setSelected}
                    onClear={() => setState((s) => ({ ...s, selected: null }))}
                    onAddNew={(name) => setState({
                        mode: 'create',
                        selected: null,
                        draft: { ...EMPTY_PASSENGER_DRAFT, name: name || '' },
                    })}
                />
            )}

            {state.mode === 'create' && (
                <PassengerCreateForm draft={state.draft} onChange={setDraft} />
            )}
        </SectionCard>
    );
}


function PassengerPicker({ customerId, selected, onSelect, onClear, onAddNew }) {
    const api = useApi();
    const [filter, setFilter] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['customer', customerId, 'passengers'],
        queryFn: () => api.get(`/customers/${customerId}/passengers`),
        enabled: !!customerId,
    });

    const passengers = data?.passengers || [];

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return passengers;
        return passengers.filter((p) =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.phone || '').toLowerCase().includes(q)
        );
    }, [passengers, filter]);

    if (selected) {
        return (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-paper-border bg-canvas">
                <div className="flex items-center gap-2 min-w-0">
                    <UserIcon className="h-5 w-5 text-ink-subtle flex-shrink-0" />
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-ink truncate">{selected.name}</div>
                        {selected.phone && (
                            <div className="text-xs text-ink-subtle truncate">{selected.phone}</div>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    className="text-xs text-ink-subtle hover:text-ink underline"
                >
                    Change
                </button>
            </div>
        );
    }

    if (isLoading) {
        return <div className="text-sm text-ink-subtle">Loading passengers…</div>;
    }

    if (passengers.length === 0) {
        return (
            <div className="p-3 rounded-lg border border-paper-border-soft bg-canvas text-sm text-ink-subtle">
                No passengers yet for this customer. Switch to "New passenger" to add one.
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" />
                <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Type to filter passengers…"
                    className="w-full text-sm pl-8 pr-3 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    autoFocus
                />
            </div>

            {filtered.length > 0 ? (
                <div className="border border-paper-border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                    {filtered.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => onSelect(p)}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-canvas text-left border-b border-paper-border-soft last:border-0"
                        >
                            <UserIcon className="h-4 w-4 text-ink-subtle flex-shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-ink truncate">{p.name}</div>
                                {p.phone && (
                                    <div className="text-xs text-ink-subtle truncate">{p.phone}</div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => onAddNew(filter.trim())}
                    className="w-full flex items-center gap-2 px-3 py-3 rounded-lg border border-dashed border-paper-border text-sm text-ink-subtle hover:text-ink hover:border-ink hover:bg-canvas/40 transition-colors"
                >
                    <Plus className="h-4 w-4 flex-shrink-0" />
                    Add new passenger{filter.trim() ? ` "${filter.trim()}"` : ''}
                </button>
            )}
        </div>
    );
}


function PassengerCreateForm({ draft, onChange }) {
    function update(field, value) {
        onChange((d) => ({ ...d, [field]: value }));
    }

    return (
        <div className="space-y-3">
            <FieldLabel label="Name" required>
                <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => update('name', e.target.value)}
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>
            <FieldLabel label="Phone">
                <input
                    type="tel"
                    value={draft.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="07000 000000"
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>
            {/* Optional — used for future direct-to-passenger notifications
                (e.g. chauffeur arrived). Matches the optional email field on
                CustomerDetailPage's passenger picker. */}
            <FieldLabel label="Email">
                <input
                    type="email"
                    value={draft.email || ''}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="Optional"
                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            </FieldLabel>
        </div>
    );
}


// =============================================================================
// RideSection
// =============================================================================
//
// Pickup → dropoff with Google Places autocomplete, mode toggle (oneway/hourly),
// date/time, vehicle class. Optional flight info block reveals on toggle.
//
// `onChangeInvalidatesQuote` is called whenever a pricing-relevant field
// changes. Page-level state then drops the stale quote so the breakdown
// disappears and the dispatcher re-clicks Calculate.
// =============================================================================

// RideSection: content-only (no SectionCard wrapper). Used inside the
// merged RideAndPricingSection. Renders the mode toggle, address inputs,
// time pickers, vehicle class, etc. Pricing-relevant changes invalidate
// any prior quote via onChangeInvalidatesQuote.
// =============================================================================
// SaveForNextTime — tick boxes under the ride: save the pickup / drop-off
// address to the customer's saved addresses, and the booking notes to their
// saved notes. Nothing is saved until the booking is created (see handleSubmit).
// Rows hide themselves when the item is empty or already saved.
// =============================================================================
function SaveForNextTime({ ride, setRide, savedAddresses = [], savedNotes = [] }) {
    const norm = (s) => (s || '').trim().toLowerCase();
    const addressSaved = (a) => savedAddresses.some((x) => norm(x.address) === norm(a));
    const noteSaved = (n) => savedNotes.some((x) => norm(x.note_text) === norm(n));

    const pickup = (ride.pickup_address || '').trim();
    const dropoff = (ride.dropoff_address || '').trim();
    const note = (ride.pax_comments || '').trim();

    const showPickup = pickup && !addressSaved(pickup);
    const showDropoff = dropoff && norm(dropoff) !== norm(pickup) && !addressSaved(dropoff);
    const showNote = note && !noteSaved(note);
    if (!showPickup && !showDropoff && !showNote) return null;

    function setAddr(key, patch) {
        setRide((r) => ({ ...r, [key]: { on: false, label: '', ...(r[key] || {}), ...patch } }));
    }

    // Plain render helper (not a component) so the label input keeps focus while typing.
    function addressRow(stateKey, title) {
        const st = ride[stateKey] || { on: false, label: '' };
        return (
            <div key={stateKey} className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-sm text-ink cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!st.on}
                        onChange={(e) => setAddr(stateKey, { on: e.target.checked })}
                    />
                    {title}
                </label>
                {st.on && (
                    <input
                        type="text"
                        value={st.label}
                        onChange={(e) => setAddr(stateKey, { label: e.target.value })}
                        placeholder="Label, e.g. Home"
                        maxLength={60}
                        className="text-xs px-2 py-1 border border-paper-border rounded-md focus:outline-none focus:border-ink w-40"
                    />
                )}
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-paper-border bg-canvas/40 p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-ink-subtle font-medium">
                Save for next time
            </div>
            {showPickup && addressRow('save_pickup', 'Save pickup address to customer')}
            {showDropoff && addressRow(
                'save_dropoff',
                ride.mode === 'hourly' ? 'Save final destination to customer' : 'Save drop-off address to customer'
            )}
            {showNote && (
                <label className="inline-flex items-center gap-1.5 text-sm text-ink cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!ride.save_note}
                        onChange={(e) => setRide((r) => ({ ...r, save_note: e.target.checked }))}
                    />
                    Save booking notes as a saved note
                </label>
            )}
        </div>
    );
}
function RideSection({ ride, setRide, liveBilling, savedAddresses = [], savedNotes = [], onChangeInvalidatesQuote }) {

    function appendSavedNote(text) {
        setRide((r) => {
            const current = (r.pax_comments || '').trim();
            const next = current ? `${current}; ${text}` : text;
            return { ...r, pax_comments: next };
        });
    }

    function update(field, value, invalidatesPricing = true) {
        setRide((r) => ({ ...r, [field]: value }));
        if (invalidatesPricing) onChangeInvalidatesQuote?.();
    }

    function setMode(newMode) {
        // Switching to one-way clears any stops (one-way doesn't allow them,
        // matching booking engine behaviour).
        setRide((r) => ({
            ...r,
            mode: newMode,
            stops: newMode === 'oneway' ? [] : r.stops,
        }));
        onChangeInvalidatesQuote?.();
    }

    // Ride is "complete enough" to continue when both addresses + vehicle
    // class are filled in. Time fields default sensibly so they're always set.
    const rideComplete = !!(ride.pickup_address && ride.dropoff_address && ride.vehicle_class);

    function setPickup(addressObj) {
        setRide((r) => ({
            ...r,
            pickup_address: addressObj?.address || '',
            pickup_address_meta: addressObj || null,
        }));
        onChangeInvalidatesQuote?.();
    }

    function setDropoff(addressObj) {
        setRide((r) => ({
            ...r,
            dropoff_address: addressObj?.address || '',
            dropoff_address_meta: addressObj || null,
        }));
        onChangeInvalidatesQuote?.();
    }

    function addStop() {
        setRide((r) => ({ ...r, stops: [...r.stops, { address: '', time: '', meta: null }] }));
    }

    function updateStopAddress(idx, addressObj) {
        setRide((r) => {
            const next = [...r.stops];
            next[idx] = {
                ...next[idx],
                address: addressObj?.address || '',
                meta: addressObj || null,
            };
            return { ...r, stops: next };
        });
        onChangeInvalidatesQuote?.();
    }

    function updateStopTime(idx, time) {
        setRide((r) => {
            const next = [...r.stops];
            next[idx] = { ...next[idx], time };
            return { ...r, stops: next };
        });
        onChangeInvalidatesQuote?.();
    }

    function removeStop(idx) {
        setRide((r) => ({ ...r, stops: r.stops.filter((_, i) => i !== idx) }));
        onChangeInvalidatesQuote?.();
    }

    return (
        <div className="space-y-4">

                {/* Mode toggle — oneway vs hourly */}
                <div className="flex gap-1 p-1 bg-canvas rounded-lg w-fit">
                    {[
                        { value: 'oneway', label: 'One-way' },
                        { value: 'hourly', label: 'Hourly hire' },
                    ].map((m) => (
                        <button
                            key={m.value}
                            type="button"
                            onClick={() => setMode(m.value)}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                ride.mode === m.value
                                    ? 'bg-paper text-ink shadow-sm'
                                    : 'text-ink-subtle hover:text-ink'
                            }`}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>

                {/* Mode-specific layout below.
                    - oneway: pickup address, dropoff address, then date+time
                    - hourly: 3 framed cards (pickup, stops, destination)  */}

                {ride.mode === 'oneway' && (
                    <>
                        <FieldLabel label="Pickup address" required>
                            <AddressInput
                                value={ride.pickup_address}
                                onChange={setPickup}
                                placeholder="Search pickup address…"
                                savedAddresses={savedAddresses}
                            />
                        </FieldLabel>

                        <FieldLabel label="Drop-off address" required>
                            <AddressInput
                                value={ride.dropoff_address}
                                onChange={setDropoff}
                                placeholder="Search drop-off address…"
                                savedAddresses={savedAddresses}
                            />
                        </FieldLabel>

                        <div className="grid grid-cols-2 gap-3">
                            <FieldLabel label="Pickup date" required>
                                <input
                                    type="date"
                                    value={ride.pickup_date}
                                    onChange={(e) => update('pickup_date', e.target.value)}
                                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                                />
                            </FieldLabel>
                            <FieldLabel label="Pickup time" required>
                                <input
                                    type="time"
                                    value={ride.pickup_time}
                                    onChange={(e) => update('pickup_time', e.target.value)}
                                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                                />
                            </FieldLabel>
                        </div>
                    </>
                )}

                {ride.mode === 'hourly' && (
                    <>
                        {/* Frame 1: PICKUP — date + time + address grouped together */}
                        <FrameCard label="PICKUP">
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <FieldLabel label="Date" required>
                                    <input
                                        type="date"
                                        value={ride.pickup_date}
                                        onChange={(e) => update('pickup_date', e.target.value)}
                                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                                    />
                                </FieldLabel>
                                <FieldLabel label="Time" required>
                                    <input
                                        type="time"
                                        value={ride.pickup_time}
                                        onChange={(e) => update('pickup_time', e.target.value)}
                                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                                    />
                                </FieldLabel>
                            </div>
                            <FieldLabel label="Pickup address" required>
                                <AddressInput
                                    value={ride.pickup_address}
                                    onChange={setPickup}
                                    placeholder="Search pickup address…"
                                    savedAddresses={savedAddresses}
                                />
                            </FieldLabel>
                        </FrameCard>

                        {/* Frame 2: STOPS — optional, with per-stop optional times */}
                        <FrameCard label="STOPS" labelSuffix="(optional)">
                            {ride.stops.length === 0 && (
                                <div className="text-xs text-ink-subtle mb-2">
                                    Add intermediate stops with optional arrival times. If a stop time is set,
                                    the final destination time is recalculated based on driving time from there.
                                </div>
                            )}

                           {ride.stops.map((stop, idx) => (
                                <div key={idx} className="rounded-lg border border-paper-border bg-paper p-3 mb-3 last:mb-0 space-y-3 shadow-sm">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] font-bold tracking-wider text-ink-subtle uppercase">
                                            Stop {idx + 1}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeStop(idx)}
                                            className="p-1 -m-1 text-ink-subtle hover:text-ink hover:bg-canvas rounded"
                                            aria-label={`Remove stop ${idx + 1}`}
                                            title="Remove stop"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                   <AddressInput
                                        value={stop.address}
                                        onChange={(addr) => updateStopAddress(idx, addr)}
                                        placeholder="Search location…"
                                        savedAddresses={savedAddresses}
                                    />
                                    <div className="flex items-center gap-3 pt-1">
                                        <label className="text-xs uppercase tracking-wide font-medium text-ink-subtle flex-shrink-0">
                                            Time
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="time"
                                                value={stop.time}
                                                onChange={(e) => updateStopTime(idx, e.target.value)}
                                                className="w-36 pr-8 text-sm px-2 py-2 border border-paper-border rounded-lg bg-paper focus:outline-none focus:border-ink"
                                            />
                                            {stop.time && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        updateStopTime(idx, '');
                                                    }}
                                                    onTouchEnd={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        updateStopTime(idx, '');
                                                    }}
                                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink p-1"
                                                    aria-label="Clear time"
                                                    title="Clear time"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={addStop}
                                className="inline-flex items-center gap-1 text-xs text-ink-subtle hover:text-ink"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                Add Stop
                            </button>
                        </FrameCard>

                        {/* Frame 3: DESTINATION — final address + final time */}
                        <FrameCard label="DESTINATION">
                            <FieldLabel label="Final destination" required>
                                <AddressInput
                                    value={ride.dropoff_address}
                                    onChange={setDropoff}
                                    placeholder="Search final destination…"
                                    savedAddresses={savedAddresses}
                                />
                            </FieldLabel>
                            <FieldLabel label="Time" required>
                                <input
                                    type="time"
                                    value={ride.final_time}
                                    onChange={(e) => update('final_time', e.target.value)}
                                    className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                                />
                            </FieldLabel>
                        </FrameCard>

                        {/* Total hire duration pill — mirrors engine line 2306 / 2447.
                            Stage 1 (instant): "X hrs"
                            Stage 2 (after Distance Matrix): "X hrs · Y mi" */}
                        <div className="flex items-center gap-2 text-sm text-ink">
                            <span className="font-medium">Total hire duration:</span>
                            <span className="inline-flex items-center px-2.5 py-1 rounded bg-ink text-paper text-xs font-bold">
                                {liveBilling.billedHours} hr{liveBilling.billedHours !== 1 ? 's' : ''}
                                {liveBilling.miles !== null && (
                                    <span className="ml-1 opacity-75">· {liveBilling.miles} mi</span>
                                )}
                            </span>
                        </div>
                    </>
                )}

                {/* Vehicle class + pax count */}
                <div className="grid grid-cols-2 gap-3">
                    <FieldLabel label="Vehicle class" required>
                        <select
                            value={ride.vehicle_class}
                            onChange={(e) => update('vehicle_class', e.target.value)}
                            className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                        >
                            <option value="business">Business Class</option>
                            <option value="first">First Class</option>
                            <option value="suv">First Class SUV</option>
                            <option value="xl">Business Class XL</option>
                        </select>
                    </FieldLabel>
                    <FieldLabel label="Passengers">
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={ride.pax_count}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d{1,2}$/.test(v)) {
                                    const n = parseInt(v, 10);
                                    if (v === '' || (n >= 0 && n <= 20)) {
                                        update('pax_count', v, false);
                                    }
                                }
                            }}
                            onBlur={() => {
                                const n = parseInt(ride.pax_count, 10);
                                if (!Number.isFinite(n) || n < 1) {
                                    update('pax_count', 1, false);
                                }
                            }}
                            className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                        />
                    </FieldLabel>
                </div>

             {/* Pax comments */}
                                <FieldLabel label="Booking notes (customer + chauffeur, optional)">
                    <textarea
                        value={ride.pax_comments}
                        onChange={(e) => update('pax_comments', e.target.value, false)}
                        placeholder="e.g. baby seat, large luggage, signboard with passenger name"
                        rows={2}
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>

                {/* Saved notes — tap to append to the chauffeur notes above.
                    Sourced from the picked customer's saved notes. Hidden when
                    none exist (e.g. new customer, or none saved yet). */}
                {savedNotes.length > 0 && (
                    <div className="-mt-2">
                        <div className="text-[11px] uppercase tracking-wide text-ink-subtle font-medium mb-1.5">
                            Saved notes — tap to add
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {savedNotes.map((n) => (
                                <button
                                    key={n.id}
                                    type="button"
                                    onClick={() => appendSavedNote(n.note_text)}
                                    title={n.note_text}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border border-paper-border bg-paper text-ink hover:bg-canvas/60 transition-colors max-w-full"
                                >
                                    <Plus className="w-3 h-3 flex-shrink-0 text-ink-subtle" />
                                    <span className="truncate">{n.label || n.note_text}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                                {/* Chauffeur-only notes — never shown to customer */}
                <FieldLabel label="Chauffeur notes (chauffeur only, optional)">
                    <textarea
                        value={ride.chauffeur_notes || ''}
                        onChange={(e) => update('chauffeur_notes', e.target.value, false)}
                        placeholder="e.g. VIP — no small talk, use rear entrance, call office on arrival"
                        rows={2}
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>

                                <SaveForNextTime
                    ride={ride}
                    setRide={setRide}
                    savedAddresses={savedAddresses}
                    savedNotes={savedNotes}
                />

                {/* Flight info — collapsible block */}
                <button
                    type="button"
                    onClick={() => setRide((r) => ({ ...r, show_flight_fields: !r.show_flight_fields }))}
                    className="inline-flex items-center gap-1 text-xs text-ink-subtle hover:text-ink"
                >
                    <Plane className="h-3.5 w-3.5" />
                    {ride.show_flight_fields ? 'Hide flight info' : 'Add flight info'}
                </button>

                {ride.show_flight_fields && (
                    <div className="grid grid-cols-3 gap-3 p-3 rounded-lg border border-paper-border-soft bg-canvas">
                        <FieldLabel label="Flight number">
                            <input
                                type="text"
                                value={ride.flight_number}
                                onChange={(e) => update('flight_number', e.target.value, false)}
                                placeholder="BA123"
                                className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                            />
                        </FieldLabel>
                        <FieldLabel label="Tail number">
                            <input
                                type="text"
                                value={ride.tail_number}
                                onChange={(e) => update('tail_number', e.target.value, false)}
                                placeholder="G-XXXX"
                                className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                            />
                        </FieldLabel>
                        <FieldLabel label="PPR number">
                            <input
                                type="text"
                                value={ride.fbo_code}
                                onChange={(e) => update('fbo_code', e.target.value, false)}
                                placeholder="FBO access code"
                                className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                            />
                        </FieldLabel>
                    </div>
                )}

                {/* Customer reference — optional PO / cost-centre / internal
                    ref the booker provides at booking time. Per-ride field
                    (corporate often uses one ref across an itinerary, but
                    storage is per-ride so each ride is independent). Does
                    NOT invalidate pricing — purely informational. */}
                <FieldLabel label="Customer reference (optional)">
                    <input
                        type="text"
                        value={ride.customer_reference || ''}
                        onChange={(e) => update('customer_reference', e.target.value, false)}
                        placeholder="e.g. PO-2026-0042, cost centre, booker's internal ref"
                        maxLength={100}
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>
        </div>
    );
}


// =============================================================================
// PricingSection
// =============================================================================
//
// Calculate button → POSTs to /pricing/calculate with the current ride state.
// Quote breakdown rendered alongside an editable price field. Dispatcher can
// type any value into the price; nothing is locked.
//
// Discrepancies between manual_price and quote.total surface as a small note —
// not a warning, just transparency. The form will record the manual value
// as quoted_price + override_reason, computed at submit time in Round 3.
// =============================================================================

// PricingSection: content-only (no SectionCard wrapper). Used inside the
// merged RideAndPricingSection alongside RideSection. Owns the Calculate
// button + price input + breakdown rendering.
function PricingSection({ ride, liveBilling, pricing, setPricing, customerId }) {
    const api = useApi();

    // "Set price manually" toggle — when on, dispatcher bypasses Calculate
    // entirely. Useful for TBC drop-offs ("as directed", "client will advise"),
    // account customers where final price is settled at invoice time, or
    // any other case where the engine can't or shouldn't compute a quote.
    const overridden = pricing.manually_overridden === true;

    function toggleManualOverride() {
        setPricing((p) => ({
            ...p,
            manually_overridden: !p.manually_overridden,
            // Clear any stale quote when entering manual mode — it shouldn't
            // be displayed, and it'd give a false signal to canSubmit if it
            // sneaks past on a later state update.
            quote: !p.manually_overridden ? null : p.quote,
            calc_error: null,
        }));
    }
    // Whether the ride has the minimum inputs needed to call the pricing API.
    // Address strings + class are always required. For hourly, we additionally
    // need a non-null miles value (Distance Matrix has resolved) so pricing
    // includes excess-miles correctly.
    const canCalculate = useMemo(() => {
        if (!ride.pickup_address || !ride.dropoff_address) return false;
        if (!ride.vehicle_class) return false;
        if (ride.mode === 'hourly' && liveBilling.miles === null) return false;
        // One-way pickup and drop-off must differ. If they're the same address,
        // a one-way ride doesn't make sense — the customer wants Hourly with
        // a stop at the same location. Block Calculate so they can't blindly
        // submit a contradictory ride.
        if (ride.mode === 'oneway' && addressesAreSame(ride.pickup_address, ride.dropoff_address)) {
            return false;
        }
        return true;
    }, [ride, liveBilling]);

    // True if pickup and drop-off are the same place. We compare address
    // strings case-insensitive + trimmed; if both have geocoded coords with
    // a place_id, those win (catches "Heathrow Airport" vs "Heathrow Airport,
    // Longford"). Returns false if either address is empty.
    const sameAddressBlocking = useMemo(() => {
        return ride.mode === 'oneway'
            && ride.pickup_address
            && ride.dropoff_address
            && addressesAreSame(ride.pickup_address, ride.dropoff_address);
    }, [ride.mode, ride.pickup_address, ride.dropoff_address]);

    async function calculate() {
        setPricing((p) => ({ ...p, is_calculating: true, calc_error: null }));

        try {
            // For hourly: live state is already current (kept fresh by the
            // page-level effects). We just feed those values into the pricing API.
            //
            // For oneway: we still need to compute distance now, since oneway
            // doesn't have the live-recalc effect (it's a single leg, no need).
            let miles, minutes;
            let journeyForDisplay = null;

            if (ride.mode === 'hourly') {
                miles = liveBilling.miles;
                // Need real minutes for the long-distance cap (backend derives
                // a one-way comparison fare from distance). Fetch via Distance
                // Matrix same as oneway does. Cached per-leg so repeated
                // Calculate clicks don't re-bill the API.
                const dm = await fetchDistanceMatrix(
                    ride.pickup_address,
                    ride.dropoff_address,
                    (ride.stops || []).map((s) => s.address)
                );
                minutes = dm.minutes;
            } else {
                const distance = await fetchDistanceMatrix(
                    ride.pickup_address,
                    ride.dropoff_address,
                    []
                );
                miles = distance.miles;
                minutes = distance.minutes;
                journeyForDisplay = {
                    duration_text: distance.duration_text,
                    distance_text: distance.distance_text,
                    raw_minutes: distance.minutes,
                };
            }

            // CC zone determination — engine line 1135-1170: pickup, dropoff,
            // AND every stop is checked by re-geocoding each address through
            // Google. Any address in zone triggers the charge.
            const ccCandidateAddresses = [
                ride.pickup_address,
                ride.dropoff_address,
                ...(ride.stops || []).map((s) => s.address),
            ];
            const ccApplies = await checkCCApplies(ccCandidateAddresses);

const requestBody = {
                mode: ride.mode,
                vehicle_class: ride.vehicle_class,
                ...(customerId ? { customer_id: customerId } : {}),
                addresses: {
                    pickup: ride.pickup_address,
                    dropoff: ride.dropoff_address,
                    stops: ride.mode === 'hourly'
                        ? ride.stops.map((s) => s.address).filter((s) => s && s.trim().length > 0)
                        : [],
                    // Postcodes for flat-rate rule lookup. Pulled from the
                    // geocoded meta on each AddressInput. Backend extracts
                    // the district (W1A 1AA → W1A) and looks up the rule.
                    pickup_postcode: extractPostcode(ride.pickup_address_meta, ride.pickup_address),
                    dropoff_postcode: extractPostcode(ride.dropoff_address_meta, ride.dropoff_address),
                },
                distance: { miles, minutes },
                datetime: {
                    date: ride.pickup_date,
                    hour: parseInt((ride.pickup_time || '00:00').split(':')[0], 10),
                },
                cc: { applies: ccApplies },
            };

            // Hourly mode: use the live billed hours (kept fresh by the page-level
            // effects, including the engine's expected-final-time auto-update logic).
            if (ride.mode === 'hourly') {
                requestBody.hours = liveBilling.billedHours;
            }

            const quote = await api.post('/pricing/calculate', requestBody);

            setPricing({
                quote,
                manual_price: quote.total.toFixed(2),
                is_calculating: false,
                calc_error: null,
                journey: journeyForDisplay,
            });
        } catch (err) {
            setPricing((p) => ({
                ...p,
                is_calculating: false,
                calc_error: err?.message || 'Could not calculate price',
            }));
        }
    }

    function setManualPrice(value) {
        // Only allow numeric input + decimal point. Reject anything else.
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
            setPricing((p) => ({ ...p, manual_price: value }));
        }
    }

    // Dispatcher's price differs from the auto-calc → small note for transparency.
    const overrideAmount = useMemo(() => {
        if (!pricing.quote) return null;
        const manual = parseFloat(pricing.manual_price);
        if (isNaN(manual)) return null;
        const diff = manual - pricing.quote.total;
        if (Math.abs(diff) < 0.01) return null;
        return { diff, manual, quoted: pricing.quote.total };
    }, [pricing.quote, pricing.manual_price]);

    // Pricing is "complete enough" to continue once a price has been entered
    // (either auto-calculated or typed). We accept any non-zero numeric value
    // — the dispatcher might be entering an agreed-rate that's lower than the
    // suggested quote, that's fine.
    const pricingComplete = !!(pricing.manual_price && parseFloat(pricing.manual_price) > 0);

    return (
        <div className="space-y-3 pt-4 mt-4 border-t border-paper-border-soft">

            <div className="text-xs font-bold tracking-wider text-ink-subtle uppercase mb-1">
                Pricing
            </div>

              {/* Calculate button + price input — same row.
                    When "Set price manually" is on, Calculate is hidden and
                    the price field is the only input. */}
                <div className="flex items-end gap-3">
                    {!overridden && (
                        <div className="flex-shrink-0">
                            <Button
                                variant="ghost"
                                onClick={calculate}
                                disabled={!canCalculate || pricing.is_calculating}
                            >
                                {pricing.is_calculating ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Calculating…
                                    </>
                                ) : (
                                    <>
                                        <Calculator className="h-4 w-4" />
                                        Calculate price
                                    </>
                                )}
                            </Button>
                        </div>
                    )}

                    <div className="flex-1">
                        <FieldLabel label="Price (£)" required>
                            <div className="relative">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">£</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={pricing.manual_price}
                                    onChange={(e) => setManualPrice(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full text-sm pl-6 pr-3 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                                />
                            </div>
                        </FieldLabel>
                    </div>
                </div>

                {/* "Set price manually" toggle — bypasses Calculate entirely.
                    Useful for TBC drop-offs ("as directed"), account customers,
                    or any case where the engine can't compute a sensible quote. */}
                <button
                    type="button"
                    onClick={toggleManualOverride}
                    className="text-xs text-ink-subtle hover:text-ink underline"
                >
                    {overridden ? 'Use calculated price instead' : 'Set price manually'}
                </button>

              {/* Same-address warning — distinct from the generic hint
                    because it's an actual blocker the dispatcher needs to
                    resolve, not a "fill in more fields" prompt. Hidden when
                    in manual-price mode (dispatcher has explicitly opted out). */}
                {sameAddressBlocking && !overridden && (
                    <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                            A one-way ride cannot start and finish at the same location.
                            Switch to <strong>Hourly hire</strong> if the customer needs to
                            return to the pickup point.
                        </div>
                    </div>
                )}

                {/* Hint when calculate is disabled (excluding same-address case
                    which has its own warning above, and manual-override mode). */}
                {!canCalculate && !pricing.quote && !sameAddressBlocking && !overridden && (
                    <div className="text-xs text-ink-subtle">
                        {!ride.pickup_address || !ride.dropoff_address || !ride.vehicle_class
                            ? 'Fill in pickup, drop-off, and vehicle class to enable Calculate. You can also just type the price.'
                            : ride.mode === 'hourly' && liveBilling.miles === null
                                ? 'Computing route distance — Calculate will enable in a moment.'
                                : 'Ready in a moment.'}
                    </div>
                )}

                {/* Calculation error */}
                {pricing.calc_error && (
                    <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-900">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>{pricing.calc_error}</div>
                    </div>
                )}

                {/* Quote breakdown — dispatcher-only view.
                    Lines show ex-VAT amounts; VAT and final total break out
                    separately at the bottom. Customer-facing surfaces (engine,
                    portal, confirmation emails) only ever show the inclusive
                    total — never this breakdown. Hidden in manual-override mode. */}
                {pricing.quote && !overridden && (
                    <div className="rounded-lg border border-paper-border-soft bg-canvas p-3 space-y-1.5 text-xs">
                        <div className="flex items-center justify-between text-ink-subtle font-medium uppercase tracking-wide mb-1">
                            <span>Auto-calculated quote</span>
                            <span className="text-[10px] normal-case tracking-normal">All amounts ex VAT</span>
                        </div>

                        <BreakdownRow
                            label={pricing.quote.mode === 'hourly'
                                ? `${pricing.quote.base.billed_hours}h × £${pricing.quote.base.rate_per_hour}${pricing.quote.base.discount_applied ? ' (discounted)' : ''}`
                                : `Journey (${pricing.journey?.duration_text || pricing.quote.base.billed_minutes + ' min'}, ${pricing.journey?.distance_text || pricing.quote.base.billed_miles + ' mi'})`
                            }
                            value={pricing.quote.base.base_ex_vat}
                        />
                        {pricing.quote.base.mileage_ex_vat > 0 && (
                            <BreakdownRow
                                label={`Excess mileage (${pricing.quote.base.excess_miles} mi)`}
                                value={pricing.quote.base.mileage_ex_vat}
                            />
                        )}
                        {pricing.quote.surcharges.breakdown.map((s, i) => (
                            <BreakdownRow key={i} label={s.label} value={s.amount_ex_vat} />
                        ))}

                        <div className="pt-1.5 mt-1.5 border-t border-paper-border-soft flex justify-between text-ink">
                            <span className="font-medium">Subtotal (ex VAT)</span>
                            <span className="font-medium">£{Number(pricing.quote.subtotal_ex_vat).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-ink-subtle">
                            <span>VAT ({Math.round(pricing.quote.vat_rate * 100)}%)</span>
                            <span>£{Number(pricing.quote.vat_amount).toFixed(2)}</span>
                        </div>
                       {pricing.quote.applied_pricing === 'flat_rate' ? (
                            <>
                                <div className="pt-1.5 mt-1.5 border-t border-paper-border-soft flex justify-between text-ink-subtle">
                                    <span>Retail (inc VAT)</span>
                                    <span className="line-through">£{Number(pricing.quote.retail_total).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-emerald-700">
                                    <span>Flat rate applied (route-based fare)</span>
                                    <span>−£{(Number(pricing.quote.retail_total) - Number(pricing.quote.total)).toFixed(2)}</span>
                                </div>
                                {pricing.quote.discount_pct ? (
                                    <div className="text-[11px] text-ink-subtle italic mt-0.5">
                                        Customer has a {pricing.quote.discount_pct}% discount on record — flat rate is cheaper, so it took precedence.
                                    </div>
                                ) : null}
                                <div className="pt-1.5 mt-1.5 border-t border-paper-border-soft flex justify-between font-bold text-ink">
                                    <span>Total (inc VAT)</span>
                                    <span>£{pricing.quote.total.toFixed(2)}</span>
                                </div>
                            </>
                        ) : pricing.quote.applied_pricing === 'discount' ? (
                            <>
                                <div className="pt-1.5 mt-1.5 border-t border-paper-border-soft flex justify-between text-ink-subtle">
                                    <span>Retail (inc VAT)</span>
                                    <span className="line-through">£{Number(pricing.quote.retail_total).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-emerald-700">
                                    <span>Discount ({pricing.quote.discount_pct}% off fare, excl. surcharges &amp; VAT)</span>
                                    <span>−£{(Number(pricing.quote.retail_total) - Number(pricing.quote.total)).toFixed(2)}</span>
                                </div>
                                {pricing.quote.flat_rate_base_ex_vat != null ? (
                                    <div className="text-[11px] text-ink-subtle italic mt-0.5">
                                        A flat rate of £{Number(pricing.quote.flat_rate_base_ex_vat).toFixed(2)} ex VAT also matched this route — discount is cheaper, so it took precedence.
                                    </div>
                                ) : null}
                                <div className="pt-1.5 mt-1.5 border-t border-paper-border-soft flex justify-between font-bold text-ink">
                                    <span>Total (inc VAT)</span>
                                    <span>£{pricing.quote.total.toFixed(2)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="pt-1.5 mt-1.5 border-t border-paper-border-soft flex justify-between font-bold text-ink">
                                <span>Total (inc VAT)</span>
                                <span>£{pricing.quote.total.toFixed(2)}</span>
                            </div>
                        )}
                    </div>
                )}

               {/* Override note — quiet, informational only. Hidden in
                    manual-mode since there's no quote to compare against. */}
                {overrideAmount && !overridden && (
                    <div className="text-xs text-ink-subtle">
                        Manual price differs from quote by{' '}
                        <span className={overrideAmount.diff > 0 ? 'text-emerald-700' : 'text-amber-700'}>
                            {overrideAmount.diff > 0 ? '+' : ''}£{overrideAmount.diff.toFixed(2)}
                        </span>
                        {' '}— this is fine, it'll be recorded as an override.
                    </div>
                )}
        </div>
    );
}

// =============================================================================
// RidesBasket — basket of N rides, each with its own ride+pricing card
// =============================================================================
//
// Single SectionCard wrapping the rides array. Each ride renders as its own
// inner card (RideCardInBasket) — expanded if it's the active one, collapsed
// to a summary line otherwise. Below the rides:
//
//   - "+ Add another ride" button (when no ride is currently being edited)
//   - Master total input (only when rides.length > 1)
//   - Continue to payment button (only when basket is complete)
//
// Single-ride flow (rides.length === 1, never clicked "Add another ride"):
// looks identical to the previous RideAndPricingSection. Continue requires
// the one ride to be filled in + Calculate clicked. No master total field.
//
// Multi-ride flow: dispatcher clicks "Add another ride" after Continue on
// ride 1. That collapses ride 1 to a summary, opens a fresh ride 2 card.
// Repeat for N rides. When done with the last ride, Continue collapses it
// and shows the master total + final Continue to Payment.
// =============================================================================

function RidesBasket({
    rides, pricings, liveBillings,
    masterTotal, setMasterTotal, setMasterTotalUserEdited, sumOfRidePrices,
    activeRideIdx, setActiveRideIdx,
    addRide, duplicateRide, removeRide, updateRide, updatePricing, updateLiveBilling,
    savedAddresses = [],
    savedNotes = [],
    collapsed, onExpand, onCollapse, sectionNumber,
    customerId,
}) {
    const isMultiRide = rides.length > 1;

    // A ride is "complete" if addresses + vehicle class are filled in AND
    // Calculate has been clicked (pricings[i].quote !== null) AND a price
    // is set. Same rule as the old RideAndPricingSection.canCollapse.
 function isRideComplete(idx) {
        const r = rides[idx];
        const p = pricings[idx];
        // Manual-override mode: dropoff optional (TBC / "as directed").
        // Calculate mode: dropoff required so engine can compute distance.
        const rideOk = p.manually_overridden
            ? !!(r.pickup_address && r.vehicle_class)
            : !!(r.pickup_address && r.dropoff_address && r.vehicle_class);
        // Manual-override mode trusts the dispatcher entirely — no price
        // requirement at all. They might leave £0 for a comped ride,
        // type the price later, or settle at invoice time. Their call.
        // Calculate-mode still requires a quote + price > 0 to prevent
        // accidental £0 charges via "I forgot to click Calculate".
                const priceOk = !!p.manual_price && parseFloat(p.manual_price) > 0;
        return rideOk && priceOk;
    }

    // Basket is "complete" when every ride is complete AND no ride is
    // currently being edited (activeRideIdx === -1) AND, if multi-ride,
    // master total is set.
    const allRidesComplete = rides.every((_, i) => isRideComplete(i));
    const noActiveEdit = activeRideIdx === -1;
    const masterTotalOk = !isMultiRide
        || (masterTotal && parseFloat(masterTotal) > 0);
    const basketComplete = allRidesComplete && noActiveEdit && masterTotalOk;

    function handleRideContinue(idx) {
        // Collapse the ride. If it's the last one and the dispatcher hasn't
        // added more, drop activeRideIdx to -1 so "Add another ride" /
        // Continue to payment appear.
        setActiveRideIdx(-1);
    }

    function handleRideEdit(idx) {
        setActiveRideIdx(idx);
    }

    return (
       <SectionCard
            title={isMultiRide ? 'Itinerary & pricing' : 'Ride & pricing'}
            number="4"
            sectionNumber={sectionNumber}
            subtitle={isMultiRide
                ? `${rides.length} rides — where, when, what class, then price.`
                : 'Where, when, what class — then the price.'}
            collapsed={collapsed}
            summary={basketSummary(rides, pricings, liveBillings, masterTotal, isMultiRide)}
            onExpand={onExpand}
            canCollapse={basketComplete}
            onCollapse={onCollapse}
        >
            <div className="space-y-3">
               {rides.map((ride, idx) => (
                    <RideCardInBasket
                        key={idx}
                        index={idx}
                        totalRides={rides.length}
                        ride={ride}
                        pricing={pricings[idx]}
                        liveBilling={liveBillings[idx]}
                        setRide={(updater) => updateRide(idx, updater)}
                        setPricing={(updater) => updatePricing(idx, updater)}
                        setLiveBilling={(value) => updateLiveBilling(idx, value)}
                        savedAddresses={savedAddresses}
                        savedNotes={savedNotes}
                        expanded={activeRideIdx === idx}
                        onExpand={() => handleRideEdit(idx)}
                        onContinue={() => handleRideContinue(idx)}
                        onRemove={rides.length > 1 ? () => removeRide(idx) : null}
                        onDuplicate={() => duplicateRide(idx)}
                        complete={isRideComplete(idx)}
                        customerId={customerId}
                    />
                ))}

                {/* + Add another ride — only show when no ride is being edited
                    AND every existing ride is complete. Prevents adding a new
                    ride while one is half-filled. */}
                {activeRideIdx === -1 && allRidesComplete && (
                    <button
                        type="button"
                        onClick={addRide}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-paper-border text-sm font-medium text-ink-subtle hover:text-ink hover:border-ink hover:bg-canvas/40 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add another ride
                    </button>
                )}

                {/* Master total — only shown when multi-ride. Defaults to
                    sum of per-ride prices, dispatcher can override. */}
                {isMultiRide && activeRideIdx === -1 && (
                    <div className="rounded-lg border border-paper-border-soft bg-canvas/40 p-4 space-y-2">
                        <div className="text-xs font-bold tracking-wider text-ink-subtle uppercase">
                            Itinerary total
                        </div>
<FieldLabel label="What the customer pays for the whole itinerary (£)" required>
                            <div className="relative">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">£</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={masterTotal}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                            setMasterTotal(v);
                                            // Dispatcher took ownership — stop auto-syncing.
                                            setMasterTotalUserEdited(true);
                                        }
                                    }}
                                    placeholder="0.00"
                                    className="w-full text-sm pl-6 pr-3 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                                />
                            </div>
                        </FieldLabel>
                        {masterTotal && Math.abs(parseFloat(masterTotal) - sumOfRidePrices) > 0.01 && (
                            <div className="text-xs text-ink-subtle">
                                Differs from sum of ride prices by{' '}
                                <span className={parseFloat(masterTotal) > sumOfRidePrices ? 'text-emerald-700' : 'text-amber-700'}>
                                    {parseFloat(masterTotal) > sumOfRidePrices ? '+' : ''}£{(parseFloat(masterTotal) - sumOfRidePrices).toFixed(2)}
                                </span>
                                {' '}— that's fine, this is what'll be charged.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SectionCard>
    );
}


// =============================================================================
// RideCardInBasket — single ride's edit/summary card inside the basket
// =============================================================================
//
// When expanded: renders RideSection + PricingSection (the inner content
// from the old RideAndPricingSection, but without its outer SectionCard
// wrapper since we're now nested inside one).
//
// When collapsed: shows a one-line summary + Edit affordance + (if multi-ride)
// a Remove button.
// =============================================================================

function RideCardInBasket({
    index, totalRides, ride, pricing, liveBilling,
    setRide, setPricing, setLiveBilling,
    savedAddresses = [],
    savedNotes = [],
    expanded, onExpand, onContinue, onRemove, onDuplicate, complete,
    customerId,
}) {

    function onChangeInvalidatesQuote() {
        setPricing((p) => p.quote ? { ...p, quote: null } : p);
    }

    // Stage 1: instant naive recompute on time/stop changes (hourly only)
    useEffect(() => {
        if (ride.mode !== 'hourly') return;
        const naive = naiveHourlyBilling(ride.pickup_time, ride.final_time);
        if (naive !== null) {
            setLiveBilling({ ...liveBilling, billedHours: naive });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ride.mode, ride.pickup_time, ride.final_time]);

    // Stage 2: debounced Distance Matrix + refined billing (hourly only)
    useEffect(() => {
        if (ride.mode !== 'hourly') return;
        if (!ride.pickup_address || !ride.dropoff_address) return;

        const stopAddresses = ride.stops.map((s) => s.address);
        const allHaveAddresses = stopAddresses.every((a) => a && a.trim().length > 0);
        if (ride.stops.length > 0 && !allHaveAddresses) return;

        let cancelled = false;
        const handle = setTimeout(async () => {
            try {
                const { miles, legSeconds } = await fetchDistanceMatrix(
                    ride.pickup_address,
                    ride.dropoff_address,
                    stopAddresses
                );
                if (cancelled) return;

                const { billedHours, newFinalTime } = computeHourlyBilling({
                    pickupTime: ride.pickup_time,
                    finalTime: ride.final_time,
                    stops: ride.stops,
                    legSeconds,
                });

                setLiveBilling({ billedHours, miles });

                if (newFinalTime && newFinalTime !== ride.final_time) {
                    setRide((r) => ({ ...r, final_time: newFinalTime }));
                }

                setPricing((p) => p.quote ? { ...p, quote: null } : p);
            } catch {
                // Distance Matrix failed — keep stage-1, miles stays null.
            }
        }, 400);

        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        ride.mode,
        ride.pickup_address,
        ride.dropoff_address,
        ride.pickup_time,
        ride.final_time,
        JSON.stringify(ride.stops),
    ]);

    // Reset to floor when leaving hourly
    useEffect(() => {
        if (ride.mode !== 'hourly') {
            setLiveBilling({ billedHours: 2, miles: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ride.mode]);

    // ---- Collapsed view ----------------------------------------------------
    if (!expanded) {
        const summary = rideAndPricingSummary(ride, liveBilling, pricing);
        const isFilled = !!(ride.pickup_address && ride.dropoff_address);
        return (
            <div className="rounded-lg border border-paper-border-soft bg-paper p-3">
                <div className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded-full bg-ink text-paper text-xs font-bold px-1.5 flex-shrink-0">
                        {index + 1}
                    </span>
                    <button
                        type="button"
                        onClick={onExpand}
                        className="flex-1 min-w-0 text-left group"
                    >
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-ink-subtle uppercase tracking-wide">
                                Ride {index + 1} of {totalRides}
                            </span>
                            {complete && (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            )}
                        </div>
                        <div className="text-sm text-ink truncate mt-0.5">
                            {isFilled ? summary : 'Not filled in yet'}
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={onExpand}
                        className="text-xs text-ink-subtle hover:text-ink flex items-center gap-1 flex-shrink-0"
                        aria-label={`Edit ride ${index + 1}`}
                    >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                    </button>
                                        <button
                        type="button"
                        onClick={onDuplicate}
                        className="text-xs text-ink-subtle hover:text-ink flex items-center gap-1 flex-shrink-0"
                        aria-label={`Duplicate ride ${index + 1}`}
                        title="Duplicate this ride"
                    >
                        <Copy className="w-3.5 h-3.5" />
                        Copy
                    </button>
                    {onRemove && (
                        <button
                            type="button"
                            onClick={onRemove}
                            className="p-1 text-ink-subtle hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                            aria-label={`Remove ride ${index + 1}`}
                            title={`Remove ride ${index + 1}`}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ---- Expanded view -----------------------------------------------------
    return (
        <div className="rounded-lg border-2 border-ink/20 bg-paper p-4">
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded-full bg-ink text-paper text-xs font-bold px-1.5">
                        {index + 1}
                    </span>
                    <span className="text-sm font-semibold text-ink uppercase tracking-wide">
                        Ride {index + 1} of {totalRides}
                    </span>
                </div>
                {onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="inline-flex items-center gap-1 text-xs text-ink-subtle hover:text-red-600"
                    >
                        <X className="w-3.5 h-3.5" />
                        Remove
                    </button>
                )}
            </div>

           <RideSection
                ride={ride}
                setRide={setRide}
                liveBilling={liveBilling}
                savedAddresses={savedAddresses}
                savedNotes={savedNotes}
                onChangeInvalidatesQuote={onChangeInvalidatesQuote}
            />
          <PricingSection
                ride={ride}
                liveBilling={liveBilling}
                pricing={pricing}
                setPricing={setPricing}
                customerId={customerId}
            />
            {complete && (
                <div className="flex justify-end mt-4">
                    <button
                        type="button"
                        onClick={onContinue}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
                    >
                        Done with this ride
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
}


// =============================================================================
// RideAndPricingSection — KEPT FOR REFERENCE (no longer called)
// =============================================================================
//
// Replaced by RidesBasket above. Left in the file in case you want to roll
// back. Safe to delete once the basket flow is verified.
//
// Single SectionCard wrapping both content blocks. Continue requires both
// the ride to be filled in (addresses + class) AND a price to be entered.
// =============================================================================

function RideAndPricingSection({
    ride, setRide, liveBilling,
    pricing, setPricing,
    collapsed, onExpand, onCollapse,
}) {

    function onChangeInvalidatesQuote() {
        setPricing((p) => p.quote ? { ...p, quote: null } : p);
    }

    // Continue rule: ride filled in AND Calculate has been clicked at least
    // once for the current ride state (pricing.quote exists). Editing the
    // ride invalidates the quote, forcing dispatcher to re-Calculate before
    // they can Continue. This prevents stale prices from sneaking through
    // after a route or class change.
    //
    // The dispatcher can still override the price by typing — they just
    // need to run Calculate at least once first to acknowledge the system's
    // suggested price for the current ride.
    const rideComplete = !!(ride.pickup_address && ride.dropoff_address && ride.vehicle_class);
    const pricingComplete = !!pricing.quote
        && !!pricing.manual_price
        && parseFloat(pricing.manual_price) > 0;
    const sectionComplete = rideComplete && pricingComplete;

    return (
        <SectionCard
            title="Ride & pricing"
            number="4"
            subtitle="Where, when, what class — then the price."
            collapsed={collapsed}
            summary={rideAndPricingSummary(ride, liveBilling, pricing)}
            onExpand={onExpand}
            canCollapse={sectionComplete}
            onCollapse={onCollapse}
        >
            <RideSection
                ride={ride}
                setRide={setRide}
                liveBilling={liveBilling}
                onChangeInvalidatesQuote={onChangeInvalidatesQuote}
            />
            <PricingSection
                ride={ride}
                liveBilling={liveBilling}
                pricing={pricing}
                setPricing={setPricing}
            />
        </SectionCard>
    );
}


// =============================================================================
// PaymentSection
// =============================================================================
//
// Four payment methods, in order of preference (most preferred first):
//
//   1. saved_card  — charge customer's card on file (7d.3.2)        ← preferred
//   2. payment_link — send Stripe Checkout link (7d.3.2)
//   3. account     — invoice later (only if customer.account_billing_enabled)
//   4. external    — manual handling, requires note (renamed "Other" in UI)
//
// The order matters: card on file is the cheapest, fastest, no-chase option,
// so it's the suggested default. Account billing requires explicit customer
// flagging. "Other" is the catch-all (cash, bank transfer, agreed elsewhere).
//
// In 7d.3.1 only `account` and `external` are functional; the two Stripe
// methods render disabled with a "Coming next" badge so dispatchers can see
// they exist but can't submit them.
// =============================================================================

function PaymentSection({
    customer, booker, payment, setPayment,
    collapsed, onExpand, onCollapse, sectionNumber,
}) {
    function selectMethod(method) {
        // Auto-fill charge_timing from customer default when picking saved_card.
        // Dispatcher can override via the toggle below.
        let chargeTiming = payment.charge_timing;
        if (method === 'saved_card' && !chargeTiming) {
            chargeTiming = customerForFlags?.charge_timing || 'immediate';
        }
        if (method !== 'saved_card') {
            chargeTiming = '';
        }
        // Auto-fill payment link expiry to now+24h when picking payment_link.
        // Dispatcher can override via the picker below.
        let linkExpiry = payment.payment_link_expires_at;
        if (method === 'payment_link' && !linkExpiry) {
            linkExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        }
        if (method !== 'payment_link') {
            linkExpiry = '';
        }
        setPayment({
            method,
            external_note: method === 'external' ? payment.external_note : '',
            charge_timing: chargeTiming,
            payment_link_expires_at: linkExpiry,
        });
    }

    function setExternalNote(note) {
        setPayment((p) => ({ ...p, external_note: note }));
    }

    function setLinkExpiry(isoString) {
        setPayment((p) => ({ ...p, payment_link_expires_at: isoString }));
    }

    // Account billing is only offered for customers flagged as account_billing_enabled.
    // Pulled from the resolved customer (selected) or the create draft.
    const customerForFlags = customer.selected || customer.draft;
    const accountBillingEnabled = customerForFlags?.account_billing_enabled === true;

    // payment_link needs a booker email to send to. Pull from existing booker
    // (if dispatcher picked one) or the new-booker draft.
    const bookerEmailForLink = booker?.selected?.email || booker?.draft?.email || '';

    // Saved card is enabled only if the picked customer has at least one
    // card on file. Backend exposes has_payment_method + last4 on the
    // customer record (added in 7d.3.2.1).
    const customerHasCard = customerForFlags?.has_payment_method === true;
    const cardLast4 = customerForFlags?.default_payment_method_last4;

    const methods = [
        {
            value: 'saved_card',
            label: cardLast4
                ? `Charge card on file (\u2022\u2022\u2022\u2022 ${cardLast4})`
                : 'Charge card on file',
            description: 'Auto-charge customer\u2019s default card',
            disabled: !customerHasCard,
            disabledReason: customerHasCard ? null : 'Customer has no card on file',
        },
        {
            value: 'payment_link',
            label: bookerEmailForLink
                ? `Send Stripe payment link to ${bookerEmailForLink}`
                : 'Send Stripe payment link',
            description: 'Email a Stripe Checkout link to the booker',
            disabled: !bookerEmailForLink,
            disabledReason: bookerEmailForLink ? null : 'Booker has no email on file',
        },
        {
            value: 'account',
            label: 'Account billing',
            description: 'Add to invoice (settled later by accounts team)',
            disabled: !accountBillingEnabled,
            disabledReason: accountBillingEnabled
                ? null
                : 'Customer is not flagged for account billing',
        },
                {
            value: 'pay_later',
            label: 'Take payment later',
            description: 'Nothing charged now — booking waits as Pending payment until you take payment from the booking page',
            disabled: false,
        },
        {
            value: 'external',
            label: 'Other',
            description: 'Cash on the day, bank transfer, agreed separately — booking confirmed now',
            disabled: false,
        },
    ];

    // Section is "complete" enough to Continue if a method is chosen AND any
    // method-specific required fields are filled. For external, the note is
    // mandatory (matches backend schema).
    const paymentComplete = !!payment.method
        && (payment.method !== 'external' || (payment.external_note || '').trim().length > 0)
        && (payment.method !== 'saved_card' || !!payment.charge_timing)
        && (payment.method !== 'payment_link' || !!payment.payment_link_expires_at);

    return (
       <SectionCard
            title="Payment"
            number="5"
            sectionNumber={sectionNumber}
            subtitle="How is this booking being paid for?"
            collapsed={collapsed}
            summary={paymentSummary(payment)}
            onExpand={onExpand}
            canCollapse={paymentComplete}
            onCollapse={onCollapse}
        >
            <div className="space-y-2">
                {methods.map((m) => {
                    const selected = payment.method === m.value;
                    const baseClass = 'w-full text-left rounded-lg border p-3 transition-colors';
                    const stateClass = m.disabled
                        ? 'opacity-50 cursor-not-allowed border-paper-border-soft bg-paper'
                        : selected
                            ? 'border-ink bg-canvas/40'
                            : 'border-paper-border-soft bg-paper hover:bg-canvas/40';
                    return (
                        <button
                            type="button"
                            key={m.value}
                            disabled={m.disabled}
                            onClick={() => !m.disabled && selectMethod(m.value)}
                            className={`${baseClass} ${stateClass}`}
                        >
                            <div className="flex items-start gap-3">
                                <span className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                                    selected ? 'border-ink' : 'border-paper-border'
                                }`}>
                                    {selected && <span className="w-2 h-2 rounded-full bg-ink" />}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-ink">{m.label}</span>
                                        {m.disabled && m.disabledReason && (
                                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-canvas text-ink-subtle">
                                                {m.disabledReason}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-ink-subtle mt-0.5">{m.description}</div>
                                </div>
                            </div>
                        </button>
                    );
                })}

                {/* Charge timing toggle — only when saved_card selected. Auto-fills
                    from customer's default. Per-booking override stored in payment state. */}
                {payment.method === 'saved_card' && (
                    <div className="pt-2 space-y-2">
                        <FieldLabel label="When to charge">
                            <div className="space-y-1.5">
                                {[
                                    {
                                        value: 'immediate',
                                        label: 'Charge now',
                                        description: 'Card is charged immediately on booking creation',
                                    },
                                    {
                                        value: 'on_completion',
                                        label: 'Charge on ride completion',
                                        description: 'Auth held now; captured when ride completes. Voided if cancelled.',
                                    },
                                ].map((opt) => {
                                    const selected = payment.charge_timing === opt.value;
                                    return (
                                        <button
                                            type="button"
                                            key={opt.value}
                                            onClick={() => setPayment(p => ({ ...p, charge_timing: opt.value }))}
                                            className={`w-full text-left rounded-lg border p-2.5 transition-colors ${
                                                selected
                                                    ? 'border-ink bg-canvas/40'
                                                    : 'border-paper-border-soft bg-paper hover:bg-canvas/40'
                                            }`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <span className={`mt-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                                                    selected ? 'border-ink' : 'border-paper-border'
                                                }`}>
                                                    {selected && <span className="w-1.5 h-1.5 rounded-full bg-ink" />}
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-ink">{opt.label}</div>
                                                    <div className="text-xs text-ink-subtle mt-0.5">{opt.description}</div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </FieldLabel>
                    </div>
                )}

                {/* Payment link expiry picker — only when payment_link selected.
                    Defaults to now+24h. Dispatcher picks from presets or custom.
                    Stripe enforces 30min minimum, 30days maximum (we clamp on submit). */}
                {payment.method === 'payment_link' && (
                    <div className="pt-2 space-y-2">
                        <FieldLabel label="Link expires in">
                            <PaymentLinkExpiryPicker
                                valueIso={payment.payment_link_expires_at}
                                onChange={setLinkExpiry}
                            />
                        </FieldLabel>
                        <div className="text-xs text-ink-subtle">
                            After this time, the customer can&apos;t pay even if they click the link.
                            Pick a value that gives them enough time but doesn&apos;t leave the booking
                            in limbo past the pickup window.
                        </div>
                    </div>
                )}

                {/* "Other" payment note input — required when external is selected */}
                {payment.method === 'external' && (
                    <div className="pt-2">
                        <FieldLabel label="Payment note" required>
                            <input
                                type="text"
                                value={payment.external_note}
                                onChange={(e) => setExternalNote(e.target.value)}
                                placeholder="e.g. Cash on collection, paid via bank transfer ref XYZ123"
                                className="w-full text-sm px-3 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                            />
                        </FieldLabel>
                        <div className="text-xs text-ink-subtle mt-1">
                            Goes into the audit trail \u2014 stays on the booking for reference.
                        </div>
                    </div>
                )}
            </div>
        </SectionCard>
    );
}

// =============================================================================
// PaymentLinkExpiryPicker — preset chips + custom datetime input
// =============================================================================
// Quick presets cover the common cases (1h / 6h / 24h / 3 days). "Custom..."
// reveals a datetime-local input for everything else. Backed by the same
// payment_link_expires_at ISO string in payment state.
function PaymentLinkExpiryPicker({ valueIso, onChange }) {
    // Track which preset (or 'custom') was explicitly selected. We can't
    // infer this from valueIso alone because Date.now() advances between
    // setPreset and the next render — a "1 hour" preset selected 5 minutes
    // ago no longer matches `now + 1h`. Explicit state avoids that drift.
    // Default to '24h' on first render since EMPTY_PAYMENT seeds with that.
    const [selectedKey, setSelectedKey] = useState('24h');

    const presets = [
        { key: '1h',  label: '1 hour',   ms: 60 * 60 * 1000 },
        { key: '6h',  label: '6 hours',  ms: 6 * 60 * 60 * 1000 },
        { key: '24h', label: '24 hours', ms: 24 * 60 * 60 * 1000 },
        { key: '3d',  label: '3 days',   ms: 3 * 24 * 60 * 60 * 1000 },
    ];

    function setPreset(preset) {
        const iso = new Date(Date.now() + preset.ms).toISOString();
        onChange(iso);
        setSelectedKey(preset.key);
    }

    function setCustom() {
        // Switch to custom mode. If we have no value yet, seed with current
        // value (or 24h default) so the datetime input renders something.
        if (!valueIso) {
            const iso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
            onChange(iso);
        }
        setSelectedKey('custom');
    }

    function setCustomDate(local) {
        if (!local) return;
        const d = new Date(local);
        if (isNaN(d.getTime())) return;
        onChange(d.toISOString());
        // Stay in custom mode — the user is editing the date directly
        setSelectedKey('custom');
    }

    // Format ISO to datetime-local string (YYYY-MM-DDTHH:MM in local time)
    function isoToLocalInput(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
                {presets.map((p) => {
                    const selected = selectedKey === p.key;
                    return (
                        <button
                            type="button"
                            key={p.key}
                            onClick={() => setPreset(p)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                selected
                                    ? 'border-ink bg-ink text-paper'
                                    : 'border-paper-border bg-paper text-ink hover:bg-canvas/40'
                            }`}
                        >
                            {p.label}
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={setCustom}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                        selectedKey === 'custom'
                            ? 'border-ink bg-ink text-paper'
                            : 'border-paper-border bg-paper text-ink hover:bg-canvas/40'
                    }`}
                >
                    Custom…
                </button>
            </div>

            {selectedKey === 'custom' && (
                <input
                    type="datetime-local"
                    value={isoToLocalInput(valueIso)}
                    // min = now + 30 min. Stripe enforces a 30-minute
                    // minimum on Checkout Session expiry — if we let the
                    // dispatcher pick less, the backend silently bumps it,
                    // which would mismatch what was chosen.
                    min={isoToLocalInput(new Date(Date.now() + 30 * 60 * 1000).toISOString())}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                />
            )}
        </div>
    );
}


function paymentSummary(payment) {
    if (!payment.method) return 'No method selected';
    const labels = {
        saved_card: 'Charge card on file',
        payment_link: 'Send Stripe payment link',
        account: 'Account billing',
        external: 'Other',
        pay_later: 'Take payment later',
    };
    const base = labels[payment.method] || payment.method;
    if (payment.method === 'external' && payment.external_note) {
        return `${base} \u2014 ${payment.external_note}`;
    }
    if (payment.method === 'saved_card' && payment.charge_timing) {
        const timingLabel = payment.charge_timing === 'on_completion' ? 'on completion' : 'now';
        return `${base} \u2014 charge ${timingLabel}`;
    }
    if (payment.method === 'payment_link' && payment.payment_link_expires_at) {
        const exp = new Date(payment.payment_link_expires_at);
        const ms = exp.getTime() - Date.now();
        if (ms > 0) {
            const hours = Math.round(ms / (1000 * 60 * 60));
            const expLabel = hours < 24
                ? `${hours}h`
                : hours < 48
                    ? '1 day'
                    : `${Math.round(hours / 24)} days`;
            return `${base} \u2014 expires in ${expLabel}`;
        }
    }
    return base;
}


// =============================================================================
// SubmitBlock — final big "Create booking" button + any submit error
// =============================================================================

function SubmitBlock({ canSubmit, submitting, submitError, onSubmit, onEmailAsQuote }) {
    return (
        <div className="rounded-xl border border-paper-border-soft bg-paper p-5">
            {submitError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <div className="font-medium mb-0.5">Couldn&rsquo;t create booking</div>
                    <div>{submitError}</div>
                </div>
            )}

            <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit || submitting}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                    !canSubmit || submitting
                        ? 'bg-canvas text-ink-subtle cursor-not-allowed'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm'
                }`}
            >
                {submitting ? 'Creating booking…' : 'Create booking'}
                {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>

            {!canSubmit && !submitting && (
                <div className="text-xs text-ink-subtle text-center mt-2">
                    Complete every section above to enable.
                </div>
            )}

            {/* "Email as quote" — Pattern 2 escape hatch. Available
                whenever ride+pricing is filled in, even if customer/booker/
                payment isn't done yet. Lets dispatcher quote what they have
                without finalising the booking. */}
            {onEmailAsQuote && (
                <div className="mt-3 pt-3 border-t border-paper-border-soft text-center">
                    <button
                        type="button"
                        onClick={onEmailAsQuote}
                        disabled={submitting}
                        className="text-xs text-ink-subtle hover:text-ink underline"
                    >
                        <Mail className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                        Email this as a quote instead
                    </button>
                </div>
            )}
        </div>
    );
}

function BreakdownRow({ label, value }) {
    return (
        <div className="flex justify-between text-ink">
            <span className="text-ink-subtle">{label}</span>
            <span>£{Number(value).toFixed(2)}</span>
        </div>
    );
}


// =============================================================================
// Geo helpers
// =============================================================================
//
// fetchDistanceMatrix uses the Google Maps JS SDK that AddressInput already
// loads. Returns { miles, minutes } for the route from pickup to dropoff
// via any intermediate stops.
//
// Uses the Distance Matrix API (the same API the booking engine uses) rather
// than Directions API. Distance Matrix is purpose-built for "miles + minutes
// between origin and destination" and is already on the API key's allowlist.
//
// For multi-stop journeys, we make one call per leg and sum them. That's
// trivially correct and avoids the route-optimisation overhead of Directions.
// =============================================================================

// -----------------------------------------------------------------------------
// Session caches for Google Maps API calls
// -----------------------------------------------------------------------------
//
// Both caches live for the lifetime of the page (i.e. until refresh). They
// massively cut API spend during testing — same address pair / same address
// = no second call.
//
// Keys are normalised (lowercased + whitespace collapsed) so trivial typing
// differences ("Heathrow" vs " heathrow ") don't miss the cache.
//
// `_legCache` is keyed per-leg so multi-stop journeys benefit too: if you've
// previously routed Heathrow→Hyde Park and Hyde Park→Mayfair, a new journey
// Heathrow→Hyde Park→Mayfair hits cache for both legs.
//
// `_geocodeCache` stores a boolean (in-CC-zone or not) per address — much
// cheaper than caching the geocoded location since we only need the
// classification result.
const _legCache = new Map();      // key: "from|||to" → { meters, seconds }
const _geocodeCache = new Map();  // key: address     → boolean (inCCZone)

function _legCacheKey(from, to) {
    const norm = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, ' ');
    return `${norm(from)}|||${norm(to)}`;
}
function _geocodeCacheKey(addr) {
    return String(addr || '').toLowerCase().trim().replace(/\s+/g, ' ');
}

async function fetchDistanceMatrix(pickup, dropoff, stops = []) {
    if (typeof window === 'undefined' || !window.google?.maps) {
        throw new Error('Google Maps not loaded yet — please refresh and try again.');
    }

    // Build the leg list: pickup → stop1 → stop2 → ... → dropoff
    // (skipping any empty/whitespace stop addresses)
    const cleanStops = (stops || []).filter((s) => s && s.trim().length > 0);
    const legs = [];
    let prev = pickup;
    for (const stop of cleanStops) {
        legs.push([prev, stop]);
        prev = stop;
    }
    legs.push([prev, dropoff]);

    let totalMeters = 0;
    let totalSeconds = 0;
    const legSeconds = [];   // seconds per leg, in order: pickup→stop1, stop1→stop2, ..., lastStop→dropoff

    // For one-leg journeys (no stops), Google's already-formatted human-readable
    // strings ("45 mins", "30.2 mi") are kept verbatim — they're what the
    // booking engine displays on its summary line. For multi-leg journeys we
    // build our own from totals since Google only formats per-leg strings.
    let durationText = null;
    let distanceText = null;

    const service = new window.google.maps.DistanceMatrixService();

  for (const [origin, destination] of legs) {
        // Cache check — skip Google entirely if we've already routed this pair this session.
        const cacheKey = _legCacheKey(origin, destination);
        const cached = _legCache.get(cacheKey);
        let result;
        if (cached) {
            // Synthesise a result-shaped object from cached data.
            // duration_text / distance_text only matter for single-leg journeys
            // (engine displays them); for multi-leg we format from totals later
            // anyway, so empty strings here are fine.
            result = {
                duration: { value: cached.seconds, text: cached.duration_text || '' },
                distance: { value: cached.meters, text: cached.distance_text || '' },
            };
        } else {
            result = await new Promise((resolve, reject) => {
                // IMPERIAL units to match booking engine line 2280 / 2455.
                service.getDistanceMatrix({
                    origins: [origin],
                    destinations: [destination],
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    unitSystem: window.google.maps.UnitSystem.IMPERIAL,
                }, (response, status) => {
                    if (status !== 'OK') {
                        reject(new Error(`Distance Matrix returned ${status}.`));
                        return;
                    }
                    const element = response?.rows?.[0]?.elements?.[0];
                    if (!element || element.status !== 'OK') {
                        reject(new Error(
                            `Could not compute leg "${origin}" → "${destination}" (${element?.status || 'unknown'}). Check the addresses.`
                        ));
                        return;
                    }
                    resolve(element);
                });
            });

            // Save in cache for future calls in this session.
            _legCache.set(cacheKey, {
                meters: result.distance?.value || 0,
                seconds: result.duration?.value || 0,
                duration_text: result.duration?.text || '',
                distance_text: result.distance?.text || '',
            });
        }

        const seconds = result.duration?.value || 0;
        totalMeters += result.distance?.value || 0;
        totalSeconds += seconds;
        legSeconds.push(seconds);

        // Single-leg: keep Google's formatted strings (matches engine behaviour).
        if (legs.length === 1) {
            durationText = result.duration?.text || null;
            distanceText = result.distance?.text || null;
        }
    }

    // Multi-leg: format ourselves from totals (Google only gives per-leg text).
    if (legs.length > 1) {
        const totalMins = Math.ceil(totalSeconds / 60);
        const totalMiles = Math.ceil(totalMeters / 1609.34);
        if (totalMins < 60) {
            durationText = `${totalMins} mins`;
        } else {
            const h = Math.floor(totalMins / 60);
            const m = totalMins % 60;
            durationText = m === 0 ? `${h} hour${h !== 1 ? 's' : ''}` : `${h} hour${h !== 1 ? 's' : ''} ${m} mins`;
        }
        distanceText = `${totalMiles} mi`;
    }

    // Ceil rounding — matching booking engine lines 2285-2286 and 2333-2334.
    return {
        miles: Math.ceil(totalMeters / 1609.34),
        minutes: Math.ceil(totalSeconds / 60),
        duration_text: durationText,
        distance_text: distanceText,
        legSeconds,
    };
}


// CC zone polygon — same coordinates as backend pricingRates.js. Kept here
// purely for the frontend's "does CC apply?" determination so we don't have
// to do an extra round-trip just for a yes/no.
// CC zone polygon — traced from the actual TfL boundary roads.
//
// North: Pentonville Rd → Euston Rd → Marylebone Rd
// East:  City Rd → Old St → Commercial St → Mansell St → Tower Hill
// South: north bank of Thames (Tower Bridge → London Bridge → Blackfriars
//        → Waterloo → Westminster → Lambeth → Vauxhall Bridge)
// West:  Vauxhall Bridge Rd → Grosvenor Pl → Park Lane → Edgware Rd
//
// Coordinates traced clockwise from the NW corner (Edgware Rd / Marylebone Rd).
// Tested against 27 landmark addresses (Selfridges, Mayfair, Bank, Heathrow,
// Canary Wharf, etc.) — all correctly classified.
//
// Replaces the simplified 17-vertex polygon from booking engine v37.5
// (which incorrectly missed Selfridges and other addresses near the western
// boundary on Park Lane / Edgware Rd).
const CC_ZONE = [
    [51.5210, -0.1640],   // NW: Edgware Rd / Marylebone Rd
    [51.5235, -0.1430],   // Marylebone Rd / Baker St
    [51.5252, -0.1290],   // Euston Rd
    [51.5275, -0.1180],   // Euston Rd / Euston
    [51.5305, -0.1080],   // NE: Pentonville Rd / City Rd (Angel)
    [51.5260, -0.0950],   // City Rd
    [51.5247, -0.0875],   // Old St
    [51.5237, -0.0813],   // Shoreditch
    [51.5170, -0.0760],   // Commercial St
    [51.5135, -0.0723],   // Mansell St / Aldgate
    [51.5095, -0.0728],   // Mansell St / Tower
    [51.5078, -0.0760],   // Tower Hill
    [51.5063, -0.0790],   // Tower Bridge area
    [51.5072, -0.0855],   // Thames N bank — London Bridge
    [51.5085, -0.0918],   // Cannon St
    [51.5095, -0.0977],   // Blackfriars
    [51.5070, -0.1110],   // Waterloo Bridge N
    [51.5050, -0.1240],   // Embankment
    [51.4960, -0.1240],   // Westminster Bridge N
    [51.4925, -0.1260],   // Lambeth Bridge N
    [51.4895, -0.1280],   // Millbank
    [51.4870, -0.1305],   // SW: Vauxhall Bridge N
    [51.4895, -0.1370],   // Vauxhall Bridge Rd
    [51.4945, -0.1410],   // Victoria
    [51.5000, -0.1485],   // Grosvenor Pl
    [51.5030, -0.1505],   // Hyde Park Corner
    [51.5080, -0.1565],   // Park Lane
    [51.5135, -0.1590],   // Marble Arch
    [51.5180, -0.1635],   // Edgware Rd
    [51.5210, -0.1640],   // close at NW
];

// Direct port of booking engine pointInCCZone (line 949-959). Ray casting.
function pointInPolygon(lat, lng, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        if (((yi > lng) !== (yj > lng)) &&
            (lat < ((xj - xi) * (lng - yi) / (yj - yi) + xi))) {
            inside = !inside;
        }
    }
    return inside;
}

// Direct port of booking engine isAddressInCCZone (line 962-974).
// Geocodes a single address via Google Geocoder; resolves true if the result
// falls inside the CC zone polygon. 3-second timeout (resolves false).
function isAddressInCCZone(addr) {
    return new Promise((resolve) => {
        if (!addr || typeof window === 'undefined' || !window.google?.maps) {
            resolve(false);
            return;
        }

        // Cache check — same address asked twice in one session = no second geocode.
        const cacheKey = _geocodeCacheKey(addr);
        if (_geocodeCache.has(cacheKey)) {
            resolve(_geocodeCache.get(cacheKey));
            return;
        }

        let done = false;
        const timeout = setTimeout(() => {
            if (!done) { done = true; resolve(false); }
        }, 3000);
        new window.google.maps.Geocoder().geocode({ address: addr }, (results, status) => {
            if (done) return;
            clearTimeout(timeout);
            done = true;
            if (status !== 'OK' || !results[0]) {
                _geocodeCache.set(cacheKey, false);
                resolve(false);
                return;
            }
            const loc = results[0].geometry.location;
            const inZone = pointInPolygon(loc.lat(), loc.lng(), CC_ZONE);
            _geocodeCache.set(cacheKey, inZone);
            resolve(inZone);
        });
    });
}

// Direct port of booking engine line 1156-1170 logic — geocode pickup,
// dropoff, AND every stop; if any one is in the zone, CC applies.
//
// (Note: the engine has an additional fallback that checks if the *route*
// passes through the zone via DirectionsService when no individual address
// is in zone. That fallback isn't ported here because dispatch's Maps API
// key doesn't have Directions API enabled. In practice the address-level
// check covers >99% of bookings — if pickup, dropoff, or any stop is in
// the zone, that's what triggers the charge anyway.)
async function checkCCApplies(addresses) {
    const cleaned = (addresses || []).filter((a) => typeof a === 'string' && a.trim().length > 0);
    if (cleaned.length === 0) return false;
    const results = await Promise.all(cleaned.map((addr) => isAddressInCCZone(addr)));
    return results.some((inZone) => inZone === true);
}

function tomorrow() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

// -----------------------------------------------------------------------------
// Hourly billing computation
// -----------------------------------------------------------------------------
//
// Faithful port of the booking engine's hourly billing logic
// (lanz_booking_engine_37_5, lines 2330-2447 inside calculateHourlyDistance).
//
// What it does, in plain English:
//
//   1. If any stop has a time set, take the LAST timed stop's time, then add
//      the driving time of every remaining leg from that stop onwards. This
//      gives us "expectedFinalMins" — when we'd actually arrive at the final
//      destination if the dispatcher's stop time is honoured.
//
//   2. If no stop has a time, expectedFinalMins = pickup time + total driving.
//
//   3. Convert both the dispatcher's picker final-time and our expected
//      final-time into "minutes since pickup" — handles overnight bookings
//      cleanly (pickup 19:30, final 00:30 next day → picker = 300 min after
//      pickup, not -1140).
//
//   4. If expectedRel > pickerRel, push the picker forward (round up to next
//      5-minute slot). Returns the new HH:MM in `newFinalTime`.
//
//   5. Bill from pickup to the LATER of the two times (in minutes-since-pickup
//      space), with a 2-hour minimum. Ceil up.
//
// Inputs:
//   pickupTime  : 'HH:MM'
//   finalTime   : 'HH:MM'
//   stops       : [{ address, time }] — time '' means "no time set"
//   legSeconds  : [number] — seconds for each leg in order pickup→stop1, stop1→stop2,
//                  ..., lastStop→dropoff. Length = stops.length + 1.
//
// Output: { billedHours: number, newFinalTime: 'HH:MM' or null }
// -----------------------------------------------------------------------------

function computeHourlyBilling({ pickupTime, finalTime, stops, legSeconds }) {
    // Parse pickup time
    const pickupMins = timeToMins(pickupTime);
    if (pickupMins === null) {
        return { billedHours: 2, newFinalTime: null };
    }

    // ---- Step 1: determine expectedFinalMins (engine line 2342-2376) -----

    let expectedFinalMins = null;

    // Find the LAST stop that has a time set (engine line 2347-2357)
    let lastTimedStopIdx = -1;
    let lastTimedStopMins = null;
    for (let i = 0; i < (stops || []).length; i++) {
        const t = stops[i]?.time;
        const m = timeToMins(t);
        if (m !== null) {
            lastTimedStopIdx = i;
            lastTimedStopMins = m;
        }
    }

    if (lastTimedStopMins !== null) {
        // Sum leg minutes from after the last timed stop through to the
        // final destination. Each leg ceiled individually (engine line 2365).
        // legSeconds[0] = pickup→stop1, [1] = stop1→stop2, ..., [stops.length] = lastStop→final.
        let remainingMins = 0;
        for (let li = lastTimedStopIdx + 1; li <= (stops?.length || 0); li++) {
            remainingMins += Math.ceil(((legSeconds || [])[li] || 0) / 60);
        }
        expectedFinalMins = (lastTimedStopMins + remainingMins) % 1440;
    } else {
        // No stop times — use pickup time + total driving time (engine line 2371-2376).
        const totalDrivingMins = (legSeconds || []).reduce((sum, s) => sum + Math.ceil((s || 0) / 60), 0);
        expectedFinalMins = (pickupMins + totalDrivingMins) % 1440;
    }

    // ---- Step 2: relative comparison and possibly auto-update final time -

    // Both candidates expressed as "minutes since pickup" so overnight
    // bookings don't confuse the comparison (engine line 2378-2394).
    const relFromPickup = (mins) => {
        let rel = mins - pickupMins;
        if (rel < 0) rel += 1440;
        return rel;
    };

    const pickerFinalMins = timeToMins(finalTime);
    const pickerRel = pickerFinalMins !== null ? relFromPickup(pickerFinalMins) : 0;
    const expectedRel = relFromPickup(expectedFinalMins);

    let newFinalTime = null;

    // Engine condition (line 2396-2405): update if expected is later than picker.
    // We don't track _lastRawExpectedFinalMins across calls (we're not auto-recalculating
    // continuously like the engine does), so we use the simpler "expected > picker"
    // check that's correct for a one-shot calculate-on-click flow.
    if (pickerFinalMins === null || expectedRel > pickerRel) {
        // Round up to next 5-minute slot (engine line 2411-2413)
        const slots = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
        let newFH = Math.floor(expectedFinalMins / 60);
        let newFM = expectedFinalMins % 60;
        let rounded = slots.find((s) => s >= newFM);
        if (rounded === undefined) {
            rounded = 0;
            newFH = (newFH + 1) % 24;
        }
        newFinalTime = `${String(newFH).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`;
        // Update expectedFinalMins to the rounded value so the billing step uses
        // the rounded number (engine line 2424).
        expectedFinalMins = newFH * 60 + rounded;
    }

    // ---- Step 3: bill the LATER of (picker, expected) -------------------

    // Re-read picker after potential update (engine line 2429-2433).
    const finalPickerMins = newFinalTime !== null
        ? timeToMins(newFinalTime)
        : (pickerFinalMins !== null ? pickerFinalMins : pickupMins);

    const pickerSpan = (() => {
        let s = finalPickerMins - pickupMins;
        if (s < 0) s += 1440;
        return s;
    })();

    const expectedSpan = (() => {
        let s = expectedFinalMins - pickupMins;
        if (s < 0) s += 1440;
        return s;
    })();

    const effectiveSpan = Math.max(pickerSpan, expectedSpan);
    const billedHours = Math.max(2, Math.ceil(effectiveSpan / 60));

    return { billedHours, newFinalTime };
}

function timeToMins(hhmm) {
    if (!hhmm || typeof hhmm !== 'string') return null;
    const parts = hhmm.split(':');
    if (parts.length !== 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
}

// Stage 1 of the engine's hourly recalc — instant naive billing without
// distance data. Mirrors calculateHourlyDuration (engine line 2295-2306).
//
//   total = (final - pickup), wrap +1440 if negative
//   billedHours = max(2, ceil(total/60))   ← 2hr minimum charge floor
//
// Returns null if either time can't be parsed (caller keeps prior value).
function naiveHourlyBilling(pickupTime, finalTime) {
    const p = timeToMins(pickupTime);
    const f = timeToMins(finalTime);
    if (p === null || f === null) return null;
    let total = f - p;
    if (total < 0) total += 1440;
    return Math.max(2, Math.ceil(total / 60));
}


// =============================================================================
// Layout helpers
// =============================================================================

function SectionCard({
    title, number, sectionNumber, subtitle, tabs, activeTab, onTabChange, extraHeaderAction,
    collapsed, summary, onExpand,
    canCollapse, onCollapse,
    children,
}) {
    // sectionNumber overrides the static `number` prop when provided. Lets
    // callers renumber sections per mode (booking has 1-5, quote has 1-2)
    // without reaching into each section's hardcoded number.
    const displayNumber = sectionNumber ?? number;
    // Collapsed: render a compact one-line summary that's clickable to expand.
    if (collapsed) {
        return (
            <button
                type="button"
                onClick={onExpand}
                className="w-full rounded-xl border border-paper-border-soft bg-paper p-4 hover:bg-canvas/50 transition-colors text-left flex items-center gap-3 group"
            >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold flex-shrink-0">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-ink-subtle uppercase tracking-wide">
                            {displayNumber}. {title}
                        </span>
                    </div>
                    <div className="text-sm text-ink truncate mt-0.5">{summary}</div>
                </div>
                <span className="text-xs text-ink-subtle group-hover:text-ink flex items-center gap-1 flex-shrink-0">
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                </span>
            </button>
        );
    }

    // Expanded: full section with optional tabs.
    return (
        <div className="rounded-xl border border-paper-border-soft bg-paper p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                       <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-canvas text-xs font-semibold text-ink-subtle">
                            {displayNumber}
                        </span>
                        <h2 className="text-base font-semibold text-ink">{title}</h2>
                    </div>
                    {subtitle && (
                        <div className="text-xs text-ink-subtle mt-1 ml-8">{subtitle}</div>
                    )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    {extraHeaderAction}
                </div>
            </div>

            {tabs && tabs.length > 1 && (
                <div className="flex gap-1 mb-3 p-1 bg-canvas rounded-lg w-fit">
                    {tabs.map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            onClick={() => onTabChange(tab.value)}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                activeTab === tab.value
                                    ? 'bg-paper text-ink shadow-sm'
                                    : 'text-ink-subtle hover:text-ink'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div>{children}</div>

            {/* Continue button — bottom-right of the section, the visual
                progression cue after the user reads/fills the content. Filled
                primary button with forward arrow, follows the standard
                checkout/form pattern (Stripe, Typeform, etc.). Only renders
                when the section has enough data to be foldable. */}
            {canCollapse && onCollapse && (
                <div className="flex justify-end mt-4">
                    <button
                        type="button"
                        onClick={onCollapse}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
                    >
                        Continue
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
}

function FieldLabel({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs font-medium text-ink-subtle uppercase tracking-wide mb-1">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </span>
            {children}
        </label>
    );
}

// FrameCard — bordered/rounded sub-card used inside the hourly ride section
// for the 3-frame Pickup / Stops / Destination layout. Matches the booking
// engine's framed visual grouping while reading naturally inside the
// dispatch app's section cards.
function FrameCard({ label, labelSuffix, children }) {
    return (
        <div className="rounded-lg border border-paper-border-soft p-4 space-y-3">
            <div className="text-[11px] font-bold tracking-wider text-ink-subtle uppercase">
                {label}
                {labelSuffix && (
                    <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-subtle/70">
                        {labelSuffix}
                    </span>
                )}
            </div>
            {children}
        </div>
    );
}

// =============================================================================
// EmailQuoteSection — recipient details for quote-mode submit
// =============================================================================
//
// Quote mode replaces customer/booker/passenger/payment with this single
// section. We capture only what's needed to send + audit a quote: who it's
// going to (email + optional name) and how long it's valid.
//
// `validity_days` defaults to 7. Range 1-30 (locked spec). Beyond 30 = make
// a new quote. Backend computes expires_at = NOW() + validity_days * 24h.
// =============================================================================

function EmailQuoteSection({ recipient, setRecipient, collapsed, onExpand, onCollapse, sectionNumber }) {

    function update(field, value) {
        setRecipient((r) => ({ ...r, [field]: value }));
    }

    const emailValid = recipient.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.email.trim());

    return (
        <SectionCard
            title="Email quote to"
            number="2"
            sectionNumber={sectionNumber}
            subtitle="Who's getting the quote, and how long it's good for."
            collapsed={collapsed}
            summary={emailQuoteSummary(recipient)}
            onExpand={onExpand}
            canCollapse={emailValid}
            onCollapse={onCollapse}
        >
            <div className="space-y-3">
                <FieldLabel label="Email" required>
                    <input
                        type="email"
                        value={recipient.email}
                        onChange={(e) => update('email', e.target.value)}
                        placeholder="customer@example.com"
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>
                <FieldLabel label="Name (optional)">
                    <input
                        type="text"
                        value={recipient.name}
                        onChange={(e) => update('name', e.target.value)}
                        placeholder="Used in the email greeting"
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>
                <FieldLabel label="Quote valid for">
                    <select
                        value={String(recipient.validity_days)}
                        onChange={(e) => update('validity_days', parseInt(e.target.value, 10))}
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    >
                        <option value="1">1 day</option>
                        <option value="3">3 days</option>
                        <option value="7">7 days (default)</option>
                        <option value="14">14 days</option>
                        <option value="30">30 days (max)</option>
                    </select>
                </FieldLabel>
                <FieldLabel label="Notes to client (optional)">
                    <textarea
                        rows={3}
                        value={recipient.notes || ''}
                        onChange={(e) => update('notes', e.target.value)}
                        placeholder="Shown on the quote email, e.g. cancellation terms"
                        className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                    />
                </FieldLabel>
                <div className="flex flex-wrap gap-1.5 -mt-1">
                    {[24, 48, 72].map((h) => (
                        <button
                            key={h}
                            type="button"
                            onClick={() => {
                                const line = `Cancellations: full refund if cancelled more than ${h} hours before pickup.`;
                                const current = (recipient.notes || '').trimEnd();
                                update('notes', current ? `${current}\n${line}` : line);
                            }}
                            className="px-2 py-1 rounded text-xs border border-paper-border text-ink-subtle hover:text-ink hover:border-ink"
                        >
                            + Cancellation {h}h
                        </button>
                    ))}
                </div>
            </div>
        </SectionCard>
    );
}

function emailQuoteSummary(recipient) {
    if (!recipient.email) return 'No recipient yet';
    const validity = recipient.validity_days
        ? `valid ${recipient.validity_days} day${recipient.validity_days !== 1 ? 's' : ''}`
        : '';
    const parts = [recipient.email];
    if (recipient.name) parts.push(recipient.name);
    if (validity) parts.push(validity);
    return parts.join(' · ');
}

// =============================================================================
// SubmitQuoteBlock — quote-mode submit (Save draft + Email quote)
// =============================================================================

function SubmitQuoteBlock({ canSubmit, submitting, submitError, onSaveDraft, onSendQuote }) {
    return (
        <div className="rounded-xl border border-paper-border-soft bg-paper p-5">
            {submitError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <div className="font-medium mb-0.5">Couldn&rsquo;t send the quote</div>
                    <div>{submitError}</div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={onSaveDraft}
                    disabled={!canSubmit || submitting}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                        !canSubmit || submitting
                            ? 'bg-canvas text-ink-subtle cursor-not-allowed'
                            : 'bg-paper border border-paper-border text-ink hover:bg-canvas/40'
                    }`}
                >
                    Save as draft
                </button>
                <button
                    type="button"
                    onClick={onSendQuote}
                    disabled={!canSubmit || submitting}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                        !canSubmit || submitting
                            ? 'bg-canvas text-ink-subtle cursor-not-allowed'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm'
                    }`}
                >
                    {submitting ? 'Sending…' : 'Email quote'}
                    {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
            </div>

            {!canSubmit && !submitting && (
                <div className="text-xs text-ink-subtle text-center mt-2">
                    Fill in the rides, prices, and recipient email to enable.
                </div>
            )}
        </div>
    );
}

// =============================================================================
// EmailAsQuoteModal — Pattern 2: send a quote from booking-mode mid-form
// =============================================================================
//
// Doesn't touch the booking form state. Captures recipient inline and fires
// POST /quotes with action='send'. On success the modal closes; dispatcher
// can then either complete the booking or close the page entirely.
// =============================================================================

function EmailAsQuoteModal({
    rides, pricings, masterTotal,
    initialEmail, initialName,
    onCancel, onSend,
}) {
    const [email, setEmail] = useState(initialEmail || '');
    const [name, setName] = useState(initialName || '');
        const [validityDays, setValidityDays] = useState(7);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const emailValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    // Quote eligibility from current form state — every ride must be
    // priced. Same gate as the regular quote-mode submit.
    const ridesQuoteable = canSubmitQuote({
        rides,
        pricings,
        masterTotal,
        quoteRecipient: { email: 'placeholder@x.com', name: '', validity_days: validityDays },
    });

    async function handleSend(action) {
        setError(null);
        setSubmitting(true);
        try {
                        await onSend({ email: email.trim(), name: name.trim(), validity_days: validityDays, notes, action });
        } catch (err) {
            setError(err?.response?.message || err?.message || 'Could not send quote.');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-xl bg-paper p-5 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-semibold text-ink">Email as quote</h2>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-1 text-ink-subtle hover:text-ink rounded"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <p className="text-xs text-ink-subtle mb-4">
                    Send the current ride{rides.length > 1 ? 's' : ''} as a quote without taking a booking.
                    The booking form stays as it is — close the page or carry on with the booking afterwards.
                </p>

                {!ridesQuoteable && (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        Fill in and price every ride first — the quote will use whatever's there now.
                    </div>
                )}

                <div className="space-y-3">
                    <FieldLabel label="Email" required>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="customer@example.com"
                            className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                        />
                    </FieldLabel>
                    <FieldLabel label="Name (optional)">
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Used in the email greeting"
                            className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                        />
                    </FieldLabel>
                    <FieldLabel label="Quote valid for">
                        <select
                            value={String(validityDays)}
                            onChange={(e) => setValidityDays(parseInt(e.target.value, 10))}
                            className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                        >
                            <option value="1">1 day</option>
                            <option value="3">3 days</option>
                            <option value="7">7 days (default)</option>
                            <option value="14">14 days</option>
                                                       <option value="30">30 days (max)</option>
                        </select>
                    </FieldLabel>
                    <FieldLabel label="Notes to client (optional)">
                        <textarea
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Shown on the quote email, e.g. cancellation terms"
                            className="w-full text-sm px-2 py-2 border border-paper-border rounded-lg focus:outline-none focus:border-ink"
                        />
                    </FieldLabel>
                    <div className="flex flex-wrap gap-1.5 -mt-1">
                        {[24, 48, 72].map((h) => (
                            <button
                                key={h}
                                type="button"
                                onClick={() => {
                                    const line = `Cancellations: full refund if cancelled more than ${h} hours before pickup.`;
                                    const current = notes.trimEnd();
                                    setNotes(current ? `${current}\n${line}` : line);
                                }}
                                className="px-2 py-1 rounded text-xs border border-paper-border text-ink-subtle hover:text-ink hover:border-ink"
                            >
                                + Cancellation {h}h
                            </button>
                        ))}
                    </div>
                </div>

                {error && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                        {error}
                    </div>
                )}

                <div className="mt-5 flex gap-2 justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        className="px-3 py-2 text-sm text-ink-subtle hover:text-ink"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSend('draft')}
                        disabled={!emailValid || !ridesQuoteable || submitting}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            !emailValid || !ridesQuoteable || submitting
                                ? 'bg-canvas text-ink-subtle cursor-not-allowed'
                                : 'bg-paper border border-paper-border text-ink hover:bg-canvas/40'
                        }`}
                    >
                        Save as draft
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSend('send')}
                        disabled={!emailValid || !ridesQuoteable || submitting}
                        className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                            !emailValid || !ridesQuoteable || submitting
                                ? 'bg-canvas text-ink-subtle cursor-not-allowed'
                                : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                    >
                        {submitting ? 'Sending\u2026' : 'Email quote'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// Helpers
// =============================================================================

// -----------------------------------------------------------------------------
// Section "complete" predicates — used by the auto-collapse effects
// -----------------------------------------------------------------------------
//
// Each section has its own definition of "complete" — the moment at which
// it can fold and the next section can open.
//
// Customer is complete when:
//   - mode = 'pick' and a customer object is selected
//   - mode = 'create' and the draft has all required fields valid (a valid
//     email is enough for the email-blur duplicate check to have settled)
// -----------------------------------------------------------------------------

function isCustomerComplete(customer) {
    if (customer.mode === 'pick') return !!customer.selected;
    if (customer.mode === 'create') {
        const d = customer.draft;
        if (!d.first_name?.trim() || !d.last_name?.trim()) return false;
        if (!d.email?.trim()) return false;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) return false;
        return true;
    }
    return false;
}

function isBookerComplete(booker) {
    if (booker.mode === 'pick') return !!booker.selected;
    if (booker.mode === 'create') {
        const d = booker.draft;
        if (!d.name?.trim()) return false;
        if (!d.email?.trim()) return false;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) return false;
        return true;
    }
    return false;
}

function isPassengerComplete(passenger) {
    if (passenger.mode === 'same_as_booker') return true;
    if (passenger.mode === 'pick') return !!passenger.selected;
    if (passenger.mode === 'create') {
        return !!passenger.draft.name?.trim();
    }
    return false;
}

// -----------------------------------------------------------------------------
// Submit-time helpers — `canSubmit` gate + payload builder
// -----------------------------------------------------------------------------
//
// `canSubmit` returns true only when EVERY section is complete enough to
// match the backend manualBookingSchema's expectations. The sections also
// each have their own `canCollapse` predicate that gates the per-section
// Continue button — those use the same logic, just split per section.
//
// `buildSubmitPayload` shapes the form state into the request body. The
// shape mirrors backend manualBookingSchema (see schema file for full
// details). It picks the right one of (customer_id|customer object),
// (booker_id|booker object), (passenger_id|passenger|same_as_booker) and
// builds the single ride with quoted_price, breakdown, and override reason.

    function canSubmit({ customer, booker, passenger, rides, pricings, masterTotal, payment }) {
    if (!isCustomerComplete(customer)) return false;
    if (!isBookerComplete(booker)) return false;
    if (!isPassengerComplete(passenger)) return false;
   // Every ride must have route + class.
    // In Calculate mode: quote required + price > 0.
    // In manual-override mode: trust the dispatcher — no price gate at all.
    for (let i = 0; i < rides.length; i++) {
        const r = rides[i];
        const p = pricings[i];
        if (!r.pickup_address || !r.vehicle_class) return false;
        if (!p.manually_overridden && !r.dropoff_address) return false;
        if (!p.manually_overridden) {
            const price = parseFloat(p.manual_price);
            if (!price || isNaN(price) || price <= 0) return false;
        }
    }
    // Multi-ride needs a master total set.
    if (rides.length > 1) {
        const mt = parseFloat(masterTotal);
        if (!mt || isNaN(mt) || mt <= 0) return false;
    }
    if (!payment.method) return false;
    if (payment.method === 'external' && !(payment.external_note || '').trim()) return false;
    return true;
}

// -----------------------------------------------------------------------------
// Quote-mode helpers — gate + payload builder
// -----------------------------------------------------------------------------
//
// Quote needs less than booking: rides + pricings + recipient email + validity
// days. No customer/booker/passenger/payment. Backend POST /quotes accepts
// this shape directly.

function canSubmitQuote({ rides, pricings, masterTotal, quoteRecipient }) {
    // Every ride must have route + class + a price > 0. We no longer require
    // p.quote — copied rides carry a manual price without a breakdown object,
    // same as an override, and that's a valid quote.
    for (let i = 0; i < rides.length; i++) {
        const r = rides[i];
        const p = pricings[i];
        if (!r.pickup_address || !r.dropoff_address || !r.vehicle_class) return false;
        const price = parseFloat(p.manual_price);
        if (!price || isNaN(price) || price <= 0) return false;
    }
    // Multi-ride needs a master total set.
    if (rides.length > 1) {
        const mt = parseFloat(masterTotal);
        if (!mt || isNaN(mt) || mt <= 0) return false;
    }
    // Recipient email required and valid.
    const email = (quoteRecipient?.email || '').trim();
    if (!email) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    // Validity days within range.
    const days = quoteRecipient?.validity_days;
    if (!days || days < 1 || days > 30) return false;
    return true;
}

function buildQuoteSubmitPayload({
    rides, pricings, masterTotal, liveBillings,
    quoteRecipient,
    action,
}) {
    return {
        rides: rides.map((ride, i) => {
            const pricing = pricings[i];
            const quotedPrice = parseFloat(pricing.manual_price) || 0;
            return {
                mode: ride.mode,
                pickup_address: ride.pickup_address,
                dropoff_address: ride.dropoff_address,
                stops: (ride.stops || [])
                    .filter((s) => s && s.address && s.address.trim().length > 0)
                    .map((s) => ({ address: s.address.trim(), time: s.time || null })),
                pickup_date: ride.pickup_date,
                pickup_time: ride.pickup_time,
                ...(ride.mode === 'hourly' ? {
                    final_time: ride.final_time,
                                        hours: pricing.quote?.base?.billed_hours
                        || liveBillings?.[i]?.billedHours
                        || 2,
                } : {}),
                vehicle_class: ride.vehicle_class,
                vehicle_class_label: VEHICLE_CLASS_LABELS[ride.vehicle_class] || ride.vehicle_class,
                pax_count: parseInt(ride.pax_count, 10) || 1,
                pax_comments: ride.pax_comments || '',
                flight_number: ride.flight_number || '',
                tail_number: ride.tail_number || '',
                fbo_code: ride.fbo_code || '',
                ...(ride.customer_reference?.trim() ? { customer_reference: ride.customer_reference.trim() } : {}),
                quoted_price: quotedPrice,
                price_breakdown: pricing.quote || undefined,
            };
        }),
                pricings: pricings.map((p) => {
            const manual = parseFloat(p.manual_price);
            const hasManual = Number.isFinite(manual) && manual > 0;
            if (p.quote) {
                return hasManual && Math.abs(manual - Number(p.quote.total)) > 0.01
                    ? { ...p.quote, total: manual }
                    : p.quote;
            }
            return { total: hasManual ? manual : 0 };
        }),
        ...(rides.length > 1 ? { master_total: parseFloat(masterTotal) } : {}),
        customer_email: quoteRecipient.email.trim(),
                customer_name: (quoteRecipient.name || '').trim() || null,
        client_notes: (quoteRecipient.notes || '').trim() || null,
        validity_days: quoteRecipient.validity_days,
        action,
    };
}
function buildSubmitPayload({
    customer, booker, passenger,
    rides, pricings, liveBillings, masterTotal,
    payment,
    fromQuoteToken,
}) {
    const body = {};

    // Customer: existing id OR new draft.
    if (customer.mode === 'pick' && customer.selected) {
        body.customer_id = customer.selected.id;
    } else {
        body.customer = pickCustomerDraftFields(customer.draft);
    }

    // Booker: existing id OR new draft.
    if (booker.mode === 'pick' && booker.selected) {
        body.booker_id = booker.selected.id;
    } else {
        body.booker = pickBookerDraftFields(booker.draft);
    }

    // Passenger: booking-level resolver (same-as-booker | existing | new).
    // The backend uses this as the default for any ride that doesn't supply
    // its own pax_name/pax_phone. Per-ride passenger overrides come through
    // each ride's pax_name/pax_phone fields below.
    if (passenger.mode === 'same_as_booker') {
        body.passenger_same_as_booker = true;
    } else if (passenger.mode === 'pick' && passenger.selected) {
        body.passenger_id = passenger.selected.id;
    } else {
        body.passenger = pickPassengerDraftFields(passenger.draft);
    }

    // Rides — array of N. Each ride carries its own pricing + per-ride
    // pax_name / pax_phone overrides (backend prefers ride-level over
    // booking-level).
    body.rides = rides.map((ride, i) => {
        const pricing = pricings[i];
       const quotedPrice = parseFloat(pricing.manual_price) || 0;
        // override_reason fires in two cases:
        //   1. Dispatcher set price manually with no Calculate at all
        //   2. Dispatcher ran Calculate then typed a different number
        const overridden = pricing.manually_overridden
            || (pricing.quote && Math.abs(quotedPrice - pricing.quote.total) > 0.01);
        const overrideReason = pricing.manually_overridden
            ? 'Dispatcher set price manually (no auto-calculate)'
            : 'Dispatcher override (manual price)';
        return {
            mode: ride.mode,
            pickup_address: ride.pickup_address,
            dropoff_address: ride.dropoff_address,
            stops: (ride.stops || [])
                .filter((s) => s && s.address && s.address.trim().length > 0)
                .map((s) => ({
                    address: s.address.trim(),
                    time: s.time || null,
                })),
            pickup_date: ride.pickup_date,
            pickup_time: ride.pickup_time,
            ...(ride.mode === 'hourly' ? {
                final_time: ride.final_time,
                hours: pricing.quote?.base?.billed_hours
                    || liveBillings?.[i]?.billedHours
                    || 2,
            } : {}),
            vehicle_class: ride.vehicle_class,
            vehicle_class_label: VEHICLE_CLASS_LABELS[ride.vehicle_class] || ride.vehicle_class,
            // Per-ride passenger override — only sent if dispatcher edited
            // this ride's pax_name / pax_phone away from the booking-level
            // default. Empty strings get filtered out so backend falls back
            // to the booking-level passenger.
           ...(ride.pax_name ? { pax_name: ride.pax_name } : {}),
            ...(ride.pax_phone ? { pax_phone: ride.pax_phone } : {}),
                        pax_count: ride.pax_count || 1,
            pax_comments: ride.pax_comments || '',
            chauffeur_notes: ride.chauffeur_notes || '',
            flight_number: ride.flight_number || '',
            tail_number: ride.tail_number || '',
            fbo_code: ride.fbo_code || '',
            // Optional customer reference (PO / cost-centre / internal ref).
            // Sent only when filled in; backend treats empty as NULL.
            ...(ride.customer_reference?.trim() ? { customer_reference: ride.customer_reference.trim() } : {}),
            quoted_price: quotedPrice,
            price_breakdown: pricing.quote || undefined,
            ...(overridden ? { override_reason: overrideReason } : {}),
        };
    });

    // Multi-ride: send master_charged_total. Backend uses this as the
    // amount to charge Stripe; per-ride quoted_price stays as the per-ride
    // sticker. Single-ride: omit (backend uses rides[0].quoted_price).
    if (rides.length > 1) {
        body.master_charged_total = parseFloat(masterTotal);
    }

    // Payment
       // "Take payment later" is stored as external with a fixed note; the
    // backend creates it as pending_payment.
    if (payment.method === 'pay_later') {
        body.payment_method = 'external';
        body.payment_external_note = 'Payment to be taken later';
    } else {
        body.payment_method = payment.method;
    }
    if (payment.method === 'external') {
        body.payment_external_note = payment.external_note;
    }
    if (payment.method === 'saved_card') {
        body.charge_timing = payment.charge_timing;
    }
    if (payment.method === 'payment_link') {
        body.payment_link_expires_at = payment.payment_link_expires_at;
    }

    // Slice 7d.4 — convert-from-quote linkage. When set, backend stamps
    // bookings.from_quote_id on the new row and atomically converts the
    // quote inside the same transaction.
    if (fromQuoteToken) {
        body.from_quote_token = fromQuoteToken;
    }

    return body;
}

// Strip the customer draft down to schema-relevant fields.
function pickCustomerDraftFields(d) {
    return {
        customer_type: d.customer_type,
        first_name: d.first_name,
        last_name: d.last_name,
        email: d.email,
        phone: d.phone,
        company_name: d.company_name,
        notes: d.notes,
    };
}

function pickBookerDraftFields(d) {
    return {
        name: d.name,
        email: d.email,
        phone: d.phone || '',
    };
}

function pickPassengerDraftFields(d) {
    return {
        name: d.name,
        phone: d.phone || '',
        email: d.email || '',
    };
}

// -----------------------------------------------------------------------------
// Section summary strings — what shows on the collapsed line
// -----------------------------------------------------------------------------

function customerSummary(customer) {
    if (customer.mode === 'pick' && customer.selected) {
        const c = customer.selected;
        const contactPerson = customerContactPerson(c);
        const parts = [customerDisplayName(c)];
        if (contactPerson) parts.push(`Contact: ${contactPerson}`);
        if (c.email) parts.push(c.email);
        return parts.join(' · ');
    }
    if (customer.mode === 'create') {
    const d = customer.draft;
    const name = d.company_name?.trim()
        ? d.company_name.trim()
        : `${d.first_name} ${d.last_name}`.trim();
    const contactPerson = d.company_name?.trim()
        ? `${d.first_name} ${d.last_name}`.trim()
        : '';
    const parts = [`${name} (new)`];
    if (contactPerson) parts.push(`Contact: ${contactPerson}`);
    if (d.customer_type) parts.push(d.customer_type);
    if (d.email) parts.push(d.email);
    return parts.join(' · ');
}
    return '';
}

function bookerSummary(booker) {
    if (booker.mode === 'pick' && booker.selected) {
        const b = booker.selected;
        return `${b.name}${b.email ? ' · ' + b.email : ''}${b.phone ? ' · ' + b.phone : ''}`;
    }
    if (booker.mode === 'create') {
        const d = booker.draft;
        return `${d.name} (new)${d.email ? ' · ' + d.email : ''}${d.phone ? ' · ' + d.phone : ''}`;
    }
    return '';
}

function passengerSummary(passenger, booker) {
    if (passenger.mode === 'same_as_booker') {
        const bookerName = booker.selected?.name || booker.draft?.name || 'booker';
        return `Same as booker (${bookerName})`;
    }
    if (passenger.mode === 'pick' && passenger.selected) {
        const p = passenger.selected;
        return `${p.name}${p.phone ? ' · ' + p.phone : ''}`;
    }
    if (passenger.mode === 'create') {
        const d = passenger.draft;
        return `${d.name} (new)${d.phone ? ' · ' + d.phone : ''}`;
    }
    return '';
}

function rideSummary(ride, liveBilling) {
    const modeLabel = ride.mode === 'hourly'
        ? `Hourly · ${liveBilling?.billedHours || 2} hr${(liveBilling?.billedHours || 2) !== 1 ? 's' : ''}`
        : 'One-way';
    const route = ride.pickup_address && ride.dropoff_address
        ? `${shortAddr(ride.pickup_address)} → ${shortAddr(ride.dropoff_address)}`
        : 'No route set';
    const vehicleClass = VEHICLE_CLASS_LABELS[ride.vehicle_class] || ride.vehicle_class;
    const time = `${ride.pickup_date}${ride.pickup_time ? ' ' + ride.pickup_time : ''}`;
    return `${modeLabel} · ${vehicleClass} · ${route} · ${time}`;
}

function pricingSummary(pricing) {
    const price = parseFloat(pricing.manual_price);
    if (!price || isNaN(price)) return 'No price set';
    const overridden = pricing.quote && Math.abs(price - pricing.quote.total) > 0.01;
    return `£${price.toFixed(2)}${overridden ? ' (overridden)' : ''}`;
}

// Combined summary for the merged Ride & pricing section.
// Order: date/time → mode (One-way or Hourly · Nh) → vehicle class → price → route
//   "27 Apr 09:00 · One-way · Business Class · £249.00 · Heathrow → Mayfair"
//   "27 Apr 09:00 · Hourly · 4h · Business Class · £480.00 · Mayfair → Heathrow"
function rideAndPricingSummary(ride, liveBilling, pricing) {
    const dateTime = formatRideDateTime(ride);
    const mode = ride.mode === 'hourly'
        ? `Hourly · ${liveBilling?.billedHours || 2}h`
        : 'One-way';
    const vehicleClass = VEHICLE_CLASS_LABELS[ride.vehicle_class] || ride.vehicle_class;
    const price = formatRidePrice(pricing);
    const route = ride.pickup_address && ride.dropoff_address
        ? `${shortAddr(ride.pickup_address)} → ${shortAddr(ride.dropoff_address)}`
        : 'No route set';
    return `${dateTime} · ${mode} · ${vehicleClass} · ${price} · ${route}`;
}

// Basket-level summary for the collapsed Itinerary & pricing section.
// Single-ride: same as rideAndPricingSummary.
// Multi-ride: "3 rides · £450.00 total"
function basketSummary(rides, pricings, liveBillings, masterTotal, isMultiRide) {
    if (!isMultiRide) {
        return rideAndPricingSummary(rides[0], liveBillings[0], pricings[0]);
    }
    const total = parseFloat(masterTotal);
    const totalLabel = isNaN(total) ? 'no total set' : `£${total.toFixed(2)} total`;
    return `${rides.length} rides · ${totalLabel}`;
}

// Format pickup date+time for the summary line. Preferred format: "27 Apr 09:00"
function formatRideDateTime(ride) {
    if (!ride.pickup_date) return 'No date set';
    const d = new Date(ride.pickup_date + 'T00:00:00');
    if (isNaN(d.getTime())) return ride.pickup_date;
    const day = d.getDate();
    const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
    const time = ride.pickup_time || '';
    return `${day} ${month}${time ? ' ' + time : ''}`;
}

function formatRidePrice(pricing) {
    const price = parseFloat(pricing.manual_price);
    if (!price || isNaN(price)) return 'No price';
    const overridden = pricing.quote && Math.abs(price - pricing.quote.total) > 0.01;
    return `£${price.toFixed(2)}${overridden ? ' (override)' : ''}`;
}

// Compare two address strings for equivalence. Used to block one-way rides
// where pickup == dropoff (which doesn't make sense — those should be Hourly
// with a stop at the same location).
//
// Comparison: trimmed + case-insensitive. We also strip trailing UK postcode
// suffixes that Google sometimes adds inconsistently to the same place
// ("123 High St, London W1A 1AA, UK" vs "123 High St, London, UK").
function addressesAreSame(a, b) {
    if (!a || !b) return false;
    const norm = (s) => String(s)
        .toLowerCase()
        .trim()
        // Collapse repeated whitespace
        .replace(/\s+/g, ' ')
        // Strip a trailing ", UK" / ", United Kingdom"
        .replace(/,\s*(uk|united kingdom)\s*$/, '')
        // Strip trailing punctuation
        .replace(/[.,\s]+$/, '');
    return norm(a) === norm(b);
}

// Lightweight address shortener for summary lines — keeps the line readable.
// Just takes the first part of a comma-separated address.
function shortAddr(s) {
    if (!s) return '';
    return s.split(',')[0].trim();
}

const VEHICLE_CLASS_LABELS = {
    business: 'Business Class',
    first: 'First Class',
    suv: 'First Class SUV',
    xl: 'Business Class XL',
};
